<template>
    <v-container>
        <!-- Loading overlay -->
        <v-overlay v-model="overlay" color="white" opacity="1" persistent>
            <v-container style="text-align: center">
                <v-progress-circular :size="50" color="#234a98" indeterminate></v-progress-circular>
                <p class="pt-2" style="font-size: 14px; color: #234a98">
                    {{ $t('common.loading') }}
                </p>
            </v-container>
        </v-overlay>

        <!-- Main widget grid with drag & drop -->
        <draggable 
            v-model="widgets" 
            :animation="150"
            ghost-class="ghost-widget"
            @start="dragStart"
            @end="dragEnd"
            class="row"
            handle=".widget-drag-handle"
        >
            <!-- Widget Container -->
            <v-col v-for="(widget, index) in widgets" 
                   :key="widget.id" 
                   :cols="getWidgetSize(widget.size)"
                   class="widget-container"
            >
                <v-card 
                    class="widget-card"
                    :class="widget.size"
                >
                    <!-- Widget Header with management icons -->
                    <v-card-title class="widget-header">
                        <v-icon class="widget-drag-handle mr-2" color="grey">mdi-drag</v-icon>
                        <span>{{ widget.title }}</span>
                        <v-menu offset-y>
                            <template v-slot:activator="{ on, attrs }">
                                <v-btn icon small v-bind="attrs" v-on="on" class="widget-settings-btn">
                                    <v-icon>mdi-cog</v-icon>
                                </v-btn>
                            </template>
                            <v-list>
                                <v-list-item @click="editWidget(widget, index)">
                                    <v-list-item-icon><v-icon>mdi-pencil</v-icon></v-list-item-icon>
                                    <v-list-item-title>{{ $t('common.edit') }}</v-list-item-title>
                                </v-list-item>
                                <v-divider></v-divider>
                                <v-list-item-group>
                                    <v-subheader>{{ $t('dashboard.size') }}</v-subheader>
                                    <v-list-item 
                                        v-for="size in widgetSizes" 
                                        :key="size.value"
                                        @click="changeWidgetSize(widget, size.value)"
                                        :disabled="widget.size === size.value"
                                    >
                                        <v-list-item-icon>
                                            <v-icon v-if="widget.size === size.value">mdi-check</v-icon>
                                        </v-list-item-icon>
                                        <v-list-item-content>
                                            <v-list-item-title>{{ size.text }}</v-list-item-title>
                                            <v-list-item-subtitle class="text--secondary">
                                                {{ size.description }}
                                            </v-list-item-subtitle>
                                        </v-list-item-content>
                                    </v-list-item>
                                </v-list-item-group>
                                <v-divider></v-divider>
                                <v-list-item @click="removeWidget(index)" class="error--text">
                                    <v-list-item-icon><v-icon color="error">mdi-delete</v-icon></v-list-item-icon>
                                    <v-list-item-title>{{ $t('common.delete') }}</v-list-item-title>
                                </v-list-item>
                            </v-list>
                        </v-menu>
                    </v-card-title>

                    <!-- Widget Content -->
                    <v-card-text class="widget-content">
                        


                        <!-- KPI Widget -->
                        <template v-if="widget.type === 'kpi'">
                            <!-- Table-based KPI (Total Paper Consumption) -->
                            <template v-if="isTableKpi(widget.kpiType)">
                                <div class="table-kpi-container">
                                    <v-data-table
                                        :headers="getTableHeaders(widget)"
                                        :items="widget.tableData || []"
                                        :sort-by="getDefaultSortBy(widget.kpiType)"
                                        :sort-desc="true"
                                        hide-default-footer
                                        disable-pagination
                                        class="kpi-table"
                                    >
                                        <!-- Template per Refill Frequency values -->
                                        <template v-if="widget.kpiType === 'refill_frequency_avg_day'" slot="item.refillsPerDay" slot-scope="{ item }">
                                            <span class="refill-value">{{ item.refillsPerDay }}</span>
                                        </template>
                                        
                                        <!-- Template per Packs Used (Total Paper Consumption) -->
                                        <template v-if="widget.kpiType === 'total_paper_consumption'" slot="item.packsUsed" slot-scope="{ item }">
                                            <span class="packs-value">{{ item.packsUsed }}</span>
                                        </template>
                                        
                                        <!-- Template per Avg Entries (Restroom Daily Usage) -->
                                        <template v-if="widget.kpiType === 'restroom_daily_usage_avg'" slot="item.avgEntries" slot-scope="{ item }">
                                            <span class="entries-value">{{ item.avgEntries }}</span>
                                        </template>
                                        
                                        <!-- Template per Status (Restroom Daily Usage) -->
                                        <template v-if="widget.kpiType === 'restroom_daily_usage_avg'" slot="item.status" slot-scope="{ item }">
                                            <v-chip 
                                                small 
                                                :color="getStatusColor(item.status)"
                                                text-color="white"
                                                class="status-chip"
                                            >
                                                <v-icon left small>{{ getStatusIcon(item.status) }}</v-icon>
                                                {{ item.status }}
                                            </v-chip>
                                        </template>
                                        
                                        <!-- Template per Avg Alerts (Alert Activity) -->
                                        <template v-if="widget.kpiType === 'alert_activity_avg_day'" slot="item.avgAlerts" slot-scope="{ item }">
                                            <span class="alerts-value">{{ item.avgAlerts }}</span>
                                        </template>
                                        
                                        <!-- Template per trend (comune a tutti i KPI tabellari) -->
                                        <template slot="item.trend" slot-scope="{ item }">
                                            <span :class="getTrendClass(item.trend)">
                                                <v-icon 
                                                    :color="getTrendColor(item.trend)" 
                                                    size="16" 
                                                    class="mr-1"
                                                >
                                                    {{ getTrendIcon(item.trend) }}
                                                </v-icon>
                                                {{ Math.abs(item.trend).toFixed(1) }}%
                                            </span>
                                        </template>
                                    </v-data-table>
                                    
                                    <!-- Period Info -->
                                    <div class="period-info">
                                        <span class="period-label">Period: {{ widget.period || 'Last 30 days' }}</span>
                                        <span class="period-date" v-if="widget.periodEnd">Up to {{ formatDate(widget.periodEnd) }}</span>
                                    </div>
                                </div>
                            </template>
                            
                            <!-- Numeric KPI Display -->
                            <template v-else-if="isNumericKpi(widget.kpiType)">
                                <div class="kpi-container">
                                    <div class="kpi-value">{{ widget.value }}</div>
                                    <div class="kpi-label">{{ widget.label }}</div>
                                </div>
                            </template>
                            
                            <!-- Chart-based KPI (Comparisons, Heatmap) -->
                            <template v-else>
                                <apexchartComponent
                                    :type="getChartTypeForKpi(widget.kpiType)"
                                    :options="getKpiChartOptions(widget)"
                                    :series="widget.series || []"
                                    height="100%"
                                    ref="kpiChart"
                                />
                            </template>
                        </template>
                    </v-card-text>
                </v-card>
            </v-col>

            <!-- Pulsante Aggiungi Widget -->
            <v-col cols="12" sm="6" md="4">
                <v-card 
                    height="200" 
                    @click="openAddWidgetDialog" 
                    class="add-widget-card"
                    hover
                >
                    <v-card-text class="d-flex flex-column align-center justify-center">
                        <v-icon x-large class="mb-3">mdi-plus</v-icon>
                        <div class="add-widget-text">Add new widget</div>
                    </v-card-text>
                </v-card>
            </v-col>
        </draggable>

        <!-- Enhanced Edit Dialog -->
        <v-dialog v-model="editDialog" max-width="600px">
            <v-card>
                <v-card-title class="headline">
                    {{ $t('dashboard.editWidget') }}
                    <v-spacer></v-spacer>
                    <v-btn icon @click="closeEditDialog">
                        <v-icon>mdi-close</v-icon>
                    </v-btn>
                </v-card-title>

                <v-card-text>
                    <v-form ref="editForm" v-model="isEditFormValid">
                        <!-- Widget Title -->
                        <v-text-field
                            v-model="editingWidget.title"
                            label="Title"
                            outlined
                            class="mb-4"
                        ></v-text-field>

                        <!-- Widget-specific parameters -->
                        <template v-if="editingWidget.type === 'chart'">
                            <!-- Period Selection -->
                            <v-select
                                v-model="editingWidget.period"
                                :items="periodOptions"
                                label="Period"
                                outlined
                                class="mb-4"
                                @change="onEditPeriodChange"
                            ></v-select>

                            <!-- Custom Date Range -->
                            <template v-if="editingWidget.period === 'custom'">
                                <v-row>
                                    <v-col cols="6">
                                        <v-menu
                                            v-model="editStartDateMenu"
                                            :close-on-content-click="false"
                                            max-width="290px"
                                            min-width="290px"
                                        >
                                            <template v-slot:activator="{ on, attrs }">
                                                <v-text-field
                                                    v-model="editingWidget.startDate"
                                                    label="Start Date"
                                                    readonly
                                                    v-bind="attrs"
                                                    v-on="on"
                                                    outlined
                                                    dense
                                                ></v-text-field>
                                            </template>
                                            <v-date-picker
                                                v-model="editingWidget.startDate"
                                                no-title
                                                @input="editStartDateMenu = false"
                                                :max="editingWidget.endDate"
                                            ></v-date-picker>
                                        </v-menu>
                                    </v-col>
                                    <v-col cols="6">
                                        <v-menu
                                            v-model="editEndDateMenu"
                                            :close-on-content-click="false"
                                            max-width="290px"
                                            min-width="290px"
                                        >
                                            <template v-slot:activator="{ on, attrs }">
                                                <v-text-field
                                                    v-model="editingWidget.endDate"
                                                    label="End Date"
                                                    readonly
                                                    v-bind="attrs"
                                                    v-on="on"
                                                    outlined
                                                    dense
                                                ></v-text-field>
                                            </template>
                                            <v-date-picker
                                                v-model="editingWidget.endDate"
                                                no-title
                                                @input="editEndDateMenu = false"
                                                :min="editingWidget.startDate"
                                                :max="new Date().toISOString().substr(0,10)"
                                            ></v-date-picker>
                                        </v-menu>
                                    </v-col>
                                </v-row>
                            </template>

                            <!-- Entities Selection -->
                            <v-select
                                v-model="editingWidget.entities"
                                :items="availableEntities"
                                label="Entities to Compare"
                                multiple
                                chips
                                outlined
                                class="mb-4"
                            ></v-select>
                        </template>

                        <template v-else-if="editingWidget.type === 'kpi'">
                            <!-- KPI specific parameters -->
                            <v-select
                                v-model="editingWidget.dataLevel"
                                :items="kpiParameters.dataLevel"
                                label="Data Level"
                                outlined
                                class="mb-4"
                                @change="onEditDataLevelChange"
                            ></v-select>

                            <v-select
                                v-model="editingWidget.entityId"
                                :items="editAvailableEntities"
                                label="Select Entity"
                                outlined
                                class="mb-4"
                                :loading="editLoadingEntities"
                                :disabled="!editingWidget.dataLevel"
                            ></v-select>
                        </template>

                        <!-- Widget Size Selection -->
                        <v-select
                            v-model="editingWidget.size"
                            :items="widgetSizes"
                            :label="$t('dashboard.size')"
                            outlined
                            class="mb-4"
                        >
                            <template v-slot:item="{ item }">
                                <v-list-item-content>
                                    <v-list-item-title>{{ item.text }}</v-list-item-title>
                                    <v-list-item-subtitle class="text--secondary">
                                        {{ item.description }}
                                    </v-list-item-subtitle>
                                </v-list-item-content>
                            </template>
                        </v-select>
                    </v-form>
                </v-card-text>

                <v-divider></v-divider>

                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text @click="closeEditDialog">{{ $t('common.cancel') }}</v-btn>
                    <v-btn 
                        color="primary" 
                        text 
                        @click="saveWidgetChanges"
                        :disabled="!isEditFormValid"
                    >
                        {{ $t('common.saveChanges') }}
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Main dialog for adding a new widget -->
        <v-dialog v-model="widgetDialog" max-width="800px">
            <v-card>
                <v-card-title class="headline">
                    {{ currentStep === 1 ? "KPI's Widget" : 'Configure KPI Widget' }}
                    <v-spacer></v-spacer>
                    <v-btn icon @click="closeWidgetDialog">
                        <v-icon>mdi-close</v-icon>
                    </v-btn>
                </v-card-title>

                <v-card-text>
                    <!-- Step 1: KPI Type Selection -->
                    <v-window v-model="currentStep">
                        <v-window-item :value="1">
                            <v-row>
                                <v-col cols="6" v-for="kpi in kpiTypes" :key="kpi.value">
                                    <v-card
                                        class="kpi-type-card"
                                        :class="{ selected: selectedKpiType === kpi.value }"
                                        @click="selectKpiType(kpi.value)"
                                        elevation="2"
                                        height="120"
                                    >
                                        <v-card-text class="pa-3">
                                            <div class="d-flex align-center mb-2">
                                                <v-icon :color="kpi.color" class="mr-3" size="24">{{ kpi.icon }}</v-icon>
                                                <div class="kpi-title">{{ kpi.text }}</div>
                                            </div>
                                            <div class="kpi-description">{{ kpi.description }}</div>
                                        </v-card-text>
                                    </v-card>
                                </v-col>
                            </v-row>
                        </v-window-item>

                        <!-- Step 2: KPI Configuration -->
                        <v-window-item :value="2">
                            <v-form ref="form" v-model="isFormValid">
                                <v-text-field
                                    v-model="widgetTitle"
                                    label="Widget Title"
                                    outlined
                                    required
                                    class="widget-title-input"
                                    :style="{ 'font-weight': '600', 'color': '#2c3e50' }"
                                ></v-text-field>
                                    <v-select
                                        v-model="selectedDataLevel"
                                        :items="kpiParameters.dataLevel"
                                        label="Data Level"
                                        outlined
                                        required
                                        @change="onDataLevelChange"
                                    ></v-select>

                                    <!-- Hierarchical Selection based on Data Level -->
                                    
                                    <!-- Client Level: Multiple clients selection -->
                                    <template v-if="selectedDataLevel === 'client'">
                                        <v-select
                                            v-model="selectedClients"
                                            :items="availableClients"
                                            label="Select Clients"
                                            outlined
                                            required
                                            multiple
                                            chips
                                            deletable-chips
                                            :loading="loadingEntities"
                                            item-text="text"
                                            item-value="value"
                                            prepend-inner-icon="mdi-account"
                                            class="mb-3"
                                        ></v-select>
                                    </template>

                                    <!-- Building Level: Client → Building (multiple buildings only) -->
                                    <template v-if="selectedDataLevel === 'building'">
                                        <v-select
                                            v-model="selectedClient"
                                            :items="availableClients"
                                            label="Select Client"
                                            outlined
                                            required
                                            :loading="loadingEntities"
                                            item-text="text"
                                            item-value="value"
                                            prepend-inner-icon="mdi-account"
                                            class="mb-3"
                                            @change="onHierarchicalClientChange"
                                        ></v-select>

                                        <v-select
                                            v-if="selectedClient"
                                            v-model="selectedBuildings"
                                            :items="availableBuildings"
                                            label="Select Buildings"
                                            outlined
                                            required
                                            multiple
                                            chips
                                            deletable-chips
                                            :loading="loadingBuildings"
                                            item-text="text"
                                            item-value="value"
                                            prepend-inner-icon="mdi-home"
                                        ></v-select>
                                    </template>

                                    <!-- Floor Level: Client → Building → Floor (multiple floors only) -->
                                    <template v-if="selectedDataLevel === 'floor'">
                                        <v-select
                                            v-model="selectedClient"
                                            :items="availableClients"
                                            label="Select Client"
                                            outlined
                                            required
                                            :loading="loadingEntities"
                                            item-text="text"
                                            item-value="value"
                                            prepend-inner-icon="mdi-account"
                                            class="mb-3"
                                            @change="onHierarchicalClientChange"
                                        ></v-select>

                                        <v-select
                                            v-if="selectedClient"
                                            v-model="selectedBuilding"
                                            :items="availableBuildings"
                                            label="Select Building"
                                            outlined
                                            required
                                            :loading="loadingBuildings"
                                            item-text="text"
                                            item-value="value"
                                            prepend-inner-icon="mdi-home"
                                            class="mb-3"
                                            @change="onHierarchicalBuildingChange"
                                        ></v-select>

                                        <v-select
                                            v-if="selectedBuilding"
                                            v-model="selectedFloors"
                                            :items="availableFloors"
                                            label="Select Floors"
                                            outlined
                                            required
                                            multiple
                                            chips
                                            deletable-chips
                                            :loading="loadingFloors"
                                            item-text="text"
                                            item-value="value"
                                            prepend-inner-icon="mdi-floor-plan"
                                        ></v-select>
                                    </template>

                                    <!-- Area Level: Client → Building → Floor → Area (multiple areas only) -->
                                    <template v-if="selectedDataLevel === 'area'">
                                        <v-select
                                            v-model="selectedClient"
                                            :items="availableClients"
                                            label="Select Client"
                                            outlined
                                            required
                                            :loading="loadingEntities"
                                            item-text="text"
                                            item-value="value"
                                            prepend-inner-icon="mdi-account"
                                            class="mb-3"
                                            @change="onHierarchicalClientChange"
                                        ></v-select>

                                        <v-select
                                            v-if="selectedClient"
                                            v-model="selectedBuilding"
                                            :items="availableBuildings"
                                            label="Select Building"
                                            outlined
                                            required
                                            :loading="loadingBuildings"
                                            item-text="text"
                                            item-value="value"
                                            prepend-inner-icon="mdi-home"
                                            class="mb-3"
                                            @change="onHierarchicalBuildingChange"
                                        ></v-select>

                                        <v-select
                                            v-if="selectedBuilding"
                                            v-model="selectedFloor"
                                            :items="availableFloors"
                                            label="Select Floor"
                                            outlined
                                            required
                                            :loading="loadingFloors"
                                            item-text="text"
                                            item-value="value"
                                            prepend-inner-icon="mdi-floor-plan"
                                            class="mb-3"
                                            @change="onHierarchicalFloorChange"
                                        ></v-select>

                                        <v-select
                                            v-if="selectedFloor"
                                            v-model="selectedAreas"
                                            :items="availableAreas"
                                            label="Select Areas"
                                            outlined
                                            required
                                            multiple
                                            chips
                                            deletable-chips
                                            :loading="loadingAreas"
                                            item-text="text"
                                            item-value="value"
                                            prepend-inner-icon="mdi-texture-box"
                                        ></v-select>
                                    </template>

                                    <!-- Sensor Level: Single selections until sensors (multiple sensors only) -->
                                    <template v-if="selectedDataLevel === 'sensor'">
                                        <v-select
                                            v-model="selectedClient"
                                            :items="availableClients"
                                            label="Select Client"
                                            outlined
                                            required
                                            :loading="loadingEntities"
                                            item-text="text"
                                            item-value="value"
                                            prepend-inner-icon="mdi-account"
                                            class="mb-3"
                                            @change="onHierarchicalClientChange"
                                        ></v-select>

                                        <v-select
                                            v-if="selectedClient"
                                            v-model="selectedBuilding"
                                            :items="availableBuildings"
                                            label="Select Building"
                                            outlined
                                            required
                                            :loading="loadingBuildings"
                                            item-text="text"
                                            item-value="value"
                                            prepend-inner-icon="mdi-home"
                                            class="mb-3"
                                            @change="onHierarchicalBuildingChange"
                                        ></v-select>

                                        <v-select
                                            v-if="selectedBuilding"
                                            v-model="selectedFloor"
                                            :items="availableFloors"
                                            label="Select Floor"
                                            outlined
                                            required
                                            :loading="loadingFloors"
                                            item-text="text"
                                            item-value="value"
                                            prepend-inner-icon="mdi-floor-plan"
                                            class="mb-3"
                                            @change="onHierarchicalFloorChange"
                                        ></v-select>

                                        <v-select
                                            v-if="selectedFloor"
                                            v-model="selectedArea"
                                            :items="availableAreas"
                                            label="Select Area"
                                            outlined
                                            required
                                            :loading="loadingAreas"
                                            item-text="text"
                                            item-value="value"
                                            prepend-inner-icon="mdi-texture-box"
                                            class="mb-3"
                                            @change="onHierarchicalAreaChange"
                                        ></v-select>

                                        <v-select
                                            v-if="selectedArea"
                                            v-model="selectedSensors"
                                            :items="availableSensorsWithSelectAll"
                                            label="Select Sensors"
                                            outlined
                                            required
                                            multiple
                                            chips
                                            deletable-chips
                                            :loading="loadingSensors"
                                            item-text="text"
                                            item-value="value"
                                            prepend-inner-icon="mdi-access-point-network"
                                        ></v-select>
                                    </template>

                                    <!-- Period Selection -->
                                    <v-select
                                        v-model="selectedPeriod"
                                        :items="periodOptions"
                                        label="Select Period"
                                        outlined
                                        required
                                        prepend-inner-icon="mdi-calendar"
                                        class="mb-3"
                                        @change="onPeriodChange"
                                    ></v-select>

                                    <!-- Custom Period Selection -->
                                    <template v-if="selectedPeriod === 'custom'">
                                        <v-row>
                                            <v-col cols="6">
                                                <v-menu
                                                    v-model="startDateMenu"
                                                    :close-on-content-click="false"
                                                    max-width="290px"
                                                    min-width="290px"
                                                >
                                                    <template v-slot:activator="{ on, attrs }">
                                                        <v-text-field
                                                            v-model="customStartDate"
                                                            label="Start Date"
                                                            readonly
                                                            v-bind="attrs"
                                                            v-on="on"
                                                            outlined
                                                            dense
                                                            prepend-inner-icon="mdi-calendar-start"
                                                            :rules="[dateValidationRules.startDate]"
                                                        ></v-text-field>
                                                    </template>
                                                    <v-date-picker
                                                        v-model="customStartDate"
                                                        no-title
                                                        @input="startDateMenu = false"
                                                        :max="customEndDate || getYesterday()"
                                                        :min="getMinSelectableDate()"
                                                    ></v-date-picker>
                                                </v-menu>
                                            </v-col>
                                            <v-col cols="6">
                                                <v-menu
                                                    v-model="endDateMenu"
                                                    :close-on-content-click="false"
                                                    max-width="290px"
                                                    min-width="290px"
                                                >
                                                    <template v-slot:activator="{ on, attrs }">
                                                        <v-text-field
                                                            v-model="customEndDate"
                                                            label="End Date"
                                                            readonly
                                                            v-bind="attrs"
                                                            v-on="on"
                                                            outlined
                                                            dense
                                                            prepend-inner-icon="mdi-calendar-end"
                                                            :rules="[dateValidationRules.endDate]"
                                                        ></v-text-field>
                                                    </template>
                                                    <v-date-picker
                                                        v-model="customEndDate"
                                                        no-title
                                                        @input="endDateMenu = false"
                                                        :min="customStartDate"
                                                        :max="getYesterday()"
                                                    ></v-date-picker>
                                                </v-menu>
                                            </v-col>
                                        </v-row>
                                        
                                        <!-- Period Validation Info -->
                                        <v-alert 
                                            v-if="showPeriodValidation"
                                            :type="periodValidationType"
                                            text 
                                            dense 
                                            class="mb-3"
                                            border="left"
                                        >
                                            {{ periodValidationMessage }}
                                        </v-alert>
                                    </template>

                                    <!-- Period Info Display -->
                                    <v-card 
                                        outlined 
                                        class="period-info-card mb-4"
                                        v-if="selectedPeriod"
                                    >
                                        <v-card-text class="py-2">
                                            <div class="period-info-text">
                                                <v-icon small color="#666" class="mr-2">mdi-information-outline</v-icon>
                                                <span class="text--secondary">{{ getPeriodDisplayText() }}</span>
                                            </div>
                                        </v-card-text>
                                    </v-card>

                                <v-select
                                    v-model="widgetSize"
                                    :items="widgetSizes"
                                    :label="$t('dashboard.widgetSize')"
                                    outlined
                                    required
                                >
                                    <template v-slot:item="{ item }">
                                        <v-list-item-content>
                                            <v-list-item-title>{{ item.text }}</v-list-item-title>
                                            <v-list-item-subtitle class="text--secondary">
                                                {{ item.description }}
                                            </v-list-item-subtitle>
                                            <div class="font-italic">{{ item.recommendedUse }}</div>
                                        </v-list-item-content>
                                    </template>
                                </v-select>
                            </v-form>
                        </v-window-item>
                    </v-window>
                </v-card-text>

                <v-divider></v-divider>

                <v-card-actions>
                    <v-btn
                        text
                        @click="handleBackOrCancel"
                    >
                        {{ currentStep > 1 ? $t('common.back') : $t('common.cancel') }}
                    </v-btn>
                    <v-spacer></v-spacer>
                    <v-btn
                        color="primary"
                        text
                        @click="saveWidget()"
                        :disabled="currentStep === 2 && (!isFormValid || !isWidgetConfigurationComplete)"
                        v-if="currentStep === 2"
                    >
                        {{ currentStep < 3 ? $t('common.continue') : $t('common.save') }}
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-container>
</template>

<script>
import draggable from 'vuedraggable';
import axios from 'axios';

export default {
    name: 'NewDashboard',
    components: {
        draggable
    },
    data: () => ({
        overlay: false,
        widgets: [],
        widgetDialog: false,
        editingWidget: {
            title: '',
            type: null,
            size: 'medium',
            entities: [],
            dataLevel: null,
            articleType: null,
            startDate: null,
            endDate: null
        },
        drag: false,

        // Form data
        selectedWidgetType: null,
        selectedChartType: null,
        selectedParameters: [],
        selectedKpiType: null,
        widgetTitle: '',
        widgetSize: 'medium',
        selectedChartTemplate: null,
        selectedComparisonLevel: null,
        selectedEntities: [],
        selectedPeriod: null,
        startDate: null,
        endDate: null,
        startDateMenu: false,
        endDateMenu: false,
        loadingEntities: false,
        availableEntities: [],
        selectedEntity: null,

        // Available options
        widgetTypes: [
            { text: 'KPI Widget', value: 'kpi' }
        ],
        chartTypes: [
            { text: 'Line', value: 'line' },
            { text: 'Bar', value: 'bar' },
            { text: 'Area', value: 'area' }
        ],
        availableParameters: [
            { text: 'Floors', value: 'floors' },
            { text: 'Areas', value: 'areas' },
            { text: 'Periods', value: 'periods' },
            { text: 'Costs', value: 'costs' }
        ],
        kpiTypes: [
            {
                text: 'Total Paper Consumption',
                value: 'total_paper_consumption',
                description: 'Displays the total amount of paper consumed per each item over the selected time period.',
                icon: 'mdi-file-document-multiple-outline',
                format: 'sheets',
                color: '#213A8F'
            },
            {
                text: 'Refill Frequency – Avg / Day',
                value: 'refill_frequency_avg_day',
                description: 'Shows the average number of refills per dispenser in the selected timeframe.',
                icon: 'mdi-refresh-circle',
                format: 'refills/day',
                color: '#4CAF50'
            },
            {
                text: 'Restroom Daily Usage – Avg / Day',
                value: 'restroom_daily_usage_avg',
                description: 'Displays the average number of daily entries for each restroom in the selected period.',
                icon: 'mdi-door-open',
                format: 'entries/day',
                color: '#009CAB'
            },
            {
                text: 'Alert Activity – Avg / Day',
                value: 'alert_activity_avg_day',
                description: 'Displays the average number of alerts per day per device in the selected period.',
                icon: 'mdi-alert-circle-outline',
                format: 'alerts/day',
                color: '#FF9800'
            },
            {
                text: 'Paper / Soap Consumption Comparison',
                value: 'consumption_comparison',
                description: 'Compare paper usage across selected entities (e.g., buildings, areas or dispensers) to identify usage trends and disparities.',
                icon: 'mdi-chart-bar',
                format: 'comparison',
                color: '#673AB7'
            },
            {
                text: 'Usage Frequency Comparison',
                value: 'usage_frequency_comparison',
                description: 'Compare usage evolution over time between selected entities to detect behavioral patterns and peak activity periods.',
                icon: 'mdi-chart-line',
                format: 'comparison',
                color: '#3F51B5'
            },
            {
                text: 'Refill Frequency Comparison',
                value: 'refill_frequency_comparison',
                description: 'Track and compare refill frequencies over time for selected entities to spot irregularities or optimize operations.',
                icon: 'mdi-chart-timeline-variant',
                format: 'comparison',
                color: '#2196F3'
            },
            {
                text: 'Heatmap – Event Distribution Over Time',
                value: 'heatmap_event_distribution',
                description: 'Visualize when and where alerts, usage, or refill events occur across selected sensors and time periods.',
                icon: 'mdi-grid',
                format: 'heatmap',
                color: '#E91E63'
            },
            {
                text: 'Consumable Cost Analysis',
                value: 'consumable_cost_analysis',
                description: 'Visual representation of cost trends based on configured consumable pricing (price per pack set during onboarding).',
                icon: 'mdi-currency-eur',
                format: 'currency',
                color: '#4CAF50'
            }
        ],
        currentStep: 1,
        chartTemplates: [
            {
                text: 'Paper Consumption Comparison',
                value: 'paper_consumption',
                description: 'Compare paper consumption between different areas or floors over time',
                defaultType: 'bar',
                updateInterval: 'Daily update',
                preview: '/chart-previews/bar-chart.svg'
            },
            {
                text: 'Usage Trend',
                value: 'usage_trend',
                description: 'View dispenser usage trend over time',
                defaultType: 'line',
                updateInterval: 'Real-time update',
                preview: '/chart-previews/line-chart.svg'
            },
            {
                text: 'Average Daily Consumption',
                value: 'daily_consumption',
                description: 'Show average daily consumption by area or floor',
                defaultType: 'area',
                updateInterval: 'Daily update',
                preview: '/chart-previews/area-chart.svg'
            },
            {
                text: 'Alert Events',
                value: 'alert_events',
                description: 'Monitor alert events and system anomalies',
                defaultType: 'bar',
                updateInterval: 'Real-time update',
                preview: '/chart-previews/alert-chart.svg'
            },
            {
                text: 'Sensor Comparison',
                value: 'sensor_comparison',
                description: 'Compare data between selected sensors',
                defaultType: 'line',
                updateInterval: 'Real-time update',
                preview: '/chart-previews/comparison-chart.svg'
            }
        ],
        comparisonLevels: [
            { text: 'Client', value: 'client' },
            { text: 'Building', value: 'building' },
            { text: 'Floor', value: 'floor' },
            { text: 'Area', value: 'area' },
            { text: 'Sensor', value: 'sensor' }
        ],
        periodOptions: [
            { text: 'Last Day', value: 'last_day' },
            { text: 'Last 7 days', value: 'last_7_days' },
            { text: 'Last 14 days', value: 'last_14_days' },
            { text: 'Last 30 days', value: 'last_30_days' },
            { text: 'Custom Period', value: 'custom' }
        ],
        kpiParameters: {
            dataLevel: [
                { text: 'Client', value: 'client' },
                { text: 'Building', value: 'building' },
                { text: 'Floor', value: 'floor' },
                { text: 'Area', value: 'area' },
                { text: 'Sensor', value: 'sensor' }
            ],
            period: [
                { text: 'Last 7 days', value: '7d' },
                { text: 'This month', value: 'month' },
                { text: 'Custom range', value: 'custom' }
            ],
            articleType: [
                { text: 'Z Fold', value: 'z_fold' },
                { text: 'Jumbo Roll', value: 'jumbo_roll' }
            ],
            size: [
                { text: 'Small', value: 'small' },
                { text: 'Medium', value: 'medium' },
                { text: 'Large', value: 'large' }
            ]
        },
        editDialog: false,
        editingWidgetIndex: null,
        isEditFormValid: true,
        editStartDateMenu: false,
        editEndDateMenu: false,
        isDragging: false,
        editAvailableEntities: [],
        editLoadingEntities: false,
        selectedDataLevel: '',
        // Single selections for hierarchical navigation
        selectedClient: '',
        selectedBuilding: '',
        selectedFloor: '',
        selectedArea: '',
        // Multiple selections for final level
        selectedClients: [], // Used when client is the final level
        selectedBuildings: [], // Used when building is the final level  
        selectedFloors: [], // Used when floor is the final level
        selectedAreas: [], // Used when area is the final level
        selectedSensors: [], // Used when sensor is the final level
        availableClients: [],
        availableBuildings: [],
        availableFloors: [],
        availableAreas: [],
        availableSensors: [],
        // Loading states for hierarchical selection
        loadingBuildings: false,
        loadingFloors: false,
        loadingAreas: false,
        loadingSensors: false,
        // Period selection
        customStartDate: '',
        customEndDate: '',
    }),

    computed: {
        widgetSizes() {
            return [
                { 
                    text: this.$t('dashboard.sizes.small'),
                    value: 'small',
                    description: this.$t('dashboard.sizes.smallDesc'),
                    cols: 4,
                    height: 200
                },
                { 
                    text: this.$t('dashboard.sizes.medium'),
                    value: 'medium',
                    description: this.$t('dashboard.sizes.mediumDesc'),
                    cols: 6,
                    height: 350
                },
                { 
                    text: this.$t('dashboard.sizes.large'),
                    value: 'large',
                    description: this.$t('dashboard.sizes.largeDesc'),
                    cols: 12,
                    height: 500
                }
            ];
        },

        // Computed properties for "Select All" options in sensor level
        availableClientsWithSelectAll() {
            if (!this.availableClients || this.availableClients.length === 0) {
                return [];
            }
            return [
                { text: 'Select All', value: 'all' },
                ...this.availableClients
            ];
        },

        availableBuildingsWithSelectAll() {
            if (!this.availableBuildings || this.availableBuildings.length === 0) {
                return [];
            }
            return [
                { text: 'Select All', value: 'all' },
                ...this.availableBuildings
            ];
        },

        availableFloorsWithSelectAll() {
            if (!this.availableFloors || this.availableFloors.length === 0) {
                return [];
            }
            return [
                { text: 'Select All', value: 'all' },
                ...this.availableFloors
            ];
        },

        availableAreasWithSelectAll() {
            if (!this.availableAreas || this.availableAreas.length === 0) {
                return [];
            }
            return [
                { text: 'Select All', value: 'all' },
                ...this.availableAreas
            ];
        },

        availableSensorsWithSelectAll() {
            if (!this.availableSensors || this.availableSensors.length === 0) {
                return [];
            }
            return [
                { text: 'Select All', value: 'all' },
                ...this.availableSensors
            ];
        },

        // Period validation computed properties
        showPeriodValidation() {
            return this.selectedPeriod === 'custom' && this.customStartDate && this.customEndDate;
        },

        periodValidationType() {
            if (!this.customStartDate || !this.customEndDate) return 'info';
            
            const yesterday = this.getYesterday();
            const minDate = this.getMinSelectableDate();
            const diffDays = this.getDateDifference(this.customStartDate, this.customEndDate);
            
            // Controlla se le date sono nel range valido
            if (this.customEndDate > yesterday) return 'error'; // Futuro non permesso
            if (this.customStartDate < minDate) return 'error'; // Troppo indietro
            if (this.customStartDate > this.customEndDate) return 'error'; // Date invertite
            if (diffDays < 1) return 'error'; // Periodo troppo corto
            if (diffDays > 14) return 'warning'; // Periodo lungo ma valido
            
            return 'success';
        },

        periodValidationMessage() {
            if (!this.customStartDate || !this.customEndDate) return '';
            
            const yesterday = this.getYesterday();
            const minDate = this.getMinSelectableDate();
            const diffDays = this.getDateDifference(this.customStartDate, this.customEndDate);
            
            // Messaggi di errore specifici
            if (this.customEndDate > yesterday) {
                return 'End date cannot be in the future. Please select up to yesterday.';
            }
            if (this.customStartDate < minDate) {
                return 'Start date cannot be more than 30 days ago. Please select a more recent date.';
            }
            if (this.customStartDate > this.customEndDate) {
                return 'End date must be after start date.';
            }
            if (diffDays < 1) {
                return 'Period must be at least 1 day.';
            }
            if (diffDays > 14) {
                return `Period selected (${diffDays} days). Consider shorter periods for optimal performance.`;
            }
            
            return `Valid period selected (${diffDays} days).`;
        },

        dateValidationRules() {
            return {
                startDate: (value) => {
                    if (!value) return 'Start date is required';
                    return true;
                },
                endDate: (value) => {
                    if (!value) return 'End date is required';
                    if (this.customStartDate && value < this.customStartDate) {
                        return 'End date must be after start date';
                    }
                    return true;
                }
            };
        }
    },

    methods: {
        getWidgetSize(size) {
            const sizeConfig = this.widgetSizes.find(s => s.value === size);
            return sizeConfig ? sizeConfig.cols : 6;
        },

        getWidgetHeight(size) {
            const sizeConfig = this.widgetSizes.find(s => s.value === size);
            return sizeConfig ? sizeConfig.height : 350;
        },

        selectWidgetType(type) {
            this.selectedWidgetType = type;
        },

        selectChartTemplate(template) {
            this.selectedChartTemplate = template;
            const selectedTemplate = this.chartTemplates.find(t => t.value === template);
            if (selectedTemplate) {
                this.selectedChartType = selectedTemplate.defaultType;
                this.widgetTitle = selectedTemplate.text;
            }
        },

        openAddWidgetDialog() {
            this.resetForm(); // Reset completo di tutti i campi
            this.editingWidget = this.defaultWidgetState(); // Oggetto vuoto per nuovo widget
            this.selectedWidgetType = 'kpi'; // Imposta automaticamente KPI
            this.currentStep = 1;
            this.widgetDialog = true;
        },

        handleBackOrCancel() {
            if (this.currentStep > 1) {
                // Back: resetta la form e torna al passo 1
                this.resetForm();
                this.selectedWidgetType = 'kpi'; // Reimposta automaticamente KPI
                this.currentStep = 1;
            } else {
                // Cancel: resetta la form e chiudi il dialog
                this.resetForm();
                this.widgetDialog = false;
            }
        },

        closeWidgetDialog() {
            // Chiusura dialog (tasto X): resetta la form e chiudi
            this.resetForm();
            this.widgetDialog = false;
        },

        async editWidget(widget, index) {
            this.editingWidgetIndex = index;
            this.editingWidget = { ...this.defaultWidgetState(), ...widget };
            
            // Popola la form con i dati del widget esistente
            await this.populateFormFromWidget(widget);
            
            this.editDialog = true;
        },

        async populateFormFromWidget(widget) {
            // Popola i campi principali della form
            this.selectedKpiType = widget.kpiType || null;
            this.widgetTitle = widget.title || '';
            this.widgetSize = widget.size || 'medium';
            this.selectedDataLevel = widget.dataLevel || '';
            
            // Popola informazioni del periodo
            if (widget.periodType) {
                this.selectedPeriod = widget.periodType;
                if (widget.periodType === 'custom' && widget.periodStartDate && widget.periodEndDate) {
                    this.customStartDate = widget.periodStartDate;
                    this.customEndDate = widget.periodEndDate;
                }
            }
            
            // Popola le selezioni gerarchiche
            if (widget.dataLevel && widget.entityId) {
                await this.reconstructHierarchicalSelections(widget);
            }
            
            // Popola editingWidget per compatibilità con edit dialog
            this.editingWidget.dataLevel = widget.dataLevel;
            this.editingWidget.entityId = widget.entityId;
            this.editingWidget.period = widget.periodType;
            if (widget.periodType === 'custom') {
                this.editingWidget.startDate = widget.periodStartDate;
                this.editingWidget.endDate = widget.periodEndDate;
            }
        },

        async reconstructHierarchicalSelections(widget) {
            // Ricostruisce le selezioni gerarchiche dal widget esistente
            // Prima resetta le selezioni
            this.resetEntitySelections();
            
            if (!widget.dataLevel) return;
            
            try {
                // Carica sempre i clienti
                await this.loadClients();
                
                // Se abbiamo hierarchicalInfo salvate, usale per una ricostruzione accurata
                if (widget.hierarchicalInfo) {
                    await this.reconstructFromHierarchicalInfo(widget.hierarchicalInfo);
                } else {
                    // Fallback: ricostruzione semplificata usando solo entityId
                    await this.reconstructFromEntityIdOnly(widget);
                }
            } catch (error) {
                console.error('Errore nella ricostruzione delle selezioni gerarchiche:', error);
                // Se la ricostruzione fallisce, almeno imposta il data level
                this.selectedDataLevel = widget.dataLevel;
            }
        },

        async reconstructFromHierarchicalInfo(hierarchicalInfo) {
            // Ricostruzione accurata usando le informazioni gerarchiche salvate
            const info = hierarchicalInfo;
            
            // Ripristina le selezioni singole
            this.selectedClient = info.selectedClient || '';
            this.selectedBuilding = info.selectedBuilding || '';
            this.selectedFloor = info.selectedFloor || '';
            this.selectedArea = info.selectedArea || '';
            
            // Ripristina le selezioni multiple
            this.selectedClients = [...(info.selectedClients || [])];
            this.selectedBuildings = [...(info.selectedBuildings || [])];
            this.selectedFloors = [...(info.selectedFloors || [])];
            this.selectedAreas = [...(info.selectedAreas || [])];
            this.selectedSensors = [...(info.selectedSensors || [])];
            
            // Ricarica le opzioni disponibili seguendo la gerarchia
            if (this.selectedClient) {
                await this.onHierarchicalClientChange(this.selectedClient);
                
                if (this.selectedBuilding) {
                    await this.onHierarchicalBuildingChange(this.selectedBuilding);
                    
                    if (this.selectedFloor) {
                        await this.onHierarchicalFloorChange(this.selectedFloor);
                        
                        if (this.selectedArea) {
                            await this.onHierarchicalAreaChange(this.selectedArea);
                        }
                    }
                }
            }
        },

        async reconstructFromEntityIdOnly(widget) {
            // Fallback: ricostruzione semplificata per widget esistenti senza hierarchicalInfo
            switch (widget.dataLevel) {
                case 'client':
                    this.selectedClients = Array.isArray(widget.entityId) ? widget.entityId : [widget.entityId];
                    break;
                    
                case 'building':
                    // Implementazione semplificata
                    if (Array.isArray(widget.entityId) && widget.entityId.length > 0) {
                        this.selectedClient = this.getClientIdFromBuildingIds(widget.entityId);
                        await this.onHierarchicalClientChange(this.selectedClient);
                        this.selectedBuildings = widget.entityId;
                    }
                    break;
                    
                // Altri casi per floor, area, sensor potrebbero essere aggiunti se necessario
                default:
                    console.warn(`Ricostruzione limitata per dataLevel: ${widget.dataLevel}`);
            }
        },

        // Helper per la ricostruzione della gerarchia legacy
        getClientIdFromBuildingIds(buildingIds) { // eslint-disable-line no-unused-vars
            // Fallback per widget esistenti senza hierarchicalInfo
            // TODO: implementare la logica per trovare il client corretto basandosi sui buildingIds
            return this.availableClients.length > 0 ? this.availableClients[0].value : '';
        },

        getHierarchicalInfo() {
            // Salva le informazioni gerarchiche per permettere la ricostruzione durante l'editing
            return {
                dataLevel: this.selectedDataLevel,
                selectedClient: this.selectedClient,
                selectedBuilding: this.selectedBuilding,
                selectedFloor: this.selectedFloor,
                selectedArea: this.selectedArea,
                selectedClients: [...(this.selectedClients || [])],
                selectedBuildings: [...(this.selectedBuildings || [])],
                selectedFloors: [...(this.selectedFloors || [])],
                selectedAreas: [...(this.selectedAreas || [])],
                selectedSensors: [...(this.selectedSensors || [])]
            };
        },

        removeWidget(index) {
            this.widgets.splice(index, 1);
        },

        resetForm() {
            this.selectedWidgetType = null;
            this.selectedChartTemplate = null;
            this.selectedChartType = null;
            this.selectedParameters = [];
            this.selectedKpiType = null;
            this.widgetTitle = '';
            this.widgetSize = 'medium';
            this.currentStep = 1;
            this.selectedComparisonLevel = null;
            this.selectedEntities = [];
            this.selectedPeriod = '';
            this.customStartDate = '';
            this.customEndDate = '';
            this.startDate = null;
            this.endDate = null;
            this.startDateMenu = false;
            this.endDateMenu = false;
            this.loadingEntities = false;
            this.availableEntities = [];
            this.selectedEntity = null;
            // Reset data level selection
            this.selectedDataLevel = '';
            // Reset form validation
            this.isFormValid = false;
            // Reset hierarchical selection
            this.resetEntitySelections();
        },

        async saveWidget() {
            const widgetData = {
                id: this.editingWidget?.id || Date.now(),
                type: this.selectedWidgetType,
                title: this.widgetTitle,
                size: this.widgetSize
            };

            // Calcola le informazioni del client
            let selectedEntityInfo;
            if (this.selectedWidgetType === 'kpi' && this.selectedKpiType === 'total_paper_consumption') {
                selectedEntityInfo = this.getPaperConsumptionEntityInfo();
            } else {
                selectedEntityInfo = this.getSelectedEntityInfo();
            }
            widgetData.clientInfo = selectedEntityInfo.clientInfo;
            widgetData.summary = selectedEntityInfo.summary;
            widgetData.tooltipDetails = selectedEntityInfo.tooltipDetails;
            
            // Aggiungi informazioni del periodo
            const periodInfo = this.getSelectedPeriodInfo();
            if (periodInfo) {
                widgetData.periodType = periodInfo.type;
                widgetData.periodDays = periodInfo.days;
                widgetData.periodStartDate = periodInfo.startDate;
                widgetData.periodEndDate = periodInfo.endDate;
            }
            
            // Aggiungi icona e colore del KPI
            const selectedKpi = this.kpiTypes.find(k => k.value === this.selectedKpiType);
            if (selectedKpi) {
                widgetData.icon = selectedKpi.icon;
                widgetData.color = selectedKpi.color;
            }

            if (this.selectedWidgetType === 'kpi') {
                widgetData.kpiType = this.selectedKpiType;
                widgetData.dataLevel = this.selectedDataLevel;
                widgetData.entityId = this.getSelectedEntityId();
                
                // Salva informazioni gerarchiche per la ricostruzione durante l'editing
                widgetData.hierarchicalInfo = this.getHierarchicalInfo();
                
                // Salva anche i nomi testuali per le chiamate API
                widgetData.entityNames = this.getEntityNames();
                
                const kpiData = await this.fetchKpiData(widgetData);
                
                if (this.isTableKpi(this.selectedKpiType)) {
                    // Per KPI tabellari (Total Paper Consumption, Refill Frequency)
                    widgetData.tableData = kpiData.tableData || [];
                    // Use selected period info instead of hardcoded values
                    widgetData.period = this.getPeriodDisplayText();
                    widgetData.periodEnd = periodInfo ? periodInfo.endDate : new Date().toISOString();
                    widgetData.sortBy = this.getDefaultSortBy(this.selectedKpiType);
                } else if (this.isNumericKpi(this.selectedKpiType)) {
                    widgetData.value = kpiData.value;
                    widgetData.label = kpiData.label;
                } else {
                    // Per KPI basati su grafici
                    widgetData.series = kpiData.series || [];
                    widgetData.chartData = kpiData.chartData || {};
                }
            }

            // Controlla se è un widget esistente cercando l'ID nella lista
            const existingIndex = this.widgets.findIndex(w => w.id === widgetData.id);
            
            if (existingIndex !== -1) {
                this.widgets.splice(existingIndex, 1, widgetData);
            } else {
                this.widgets.push(widgetData);
            }

            this.widgetDialog = false;
            this.resetForm();
        },

        getEntityNames() {
            // Salva i nomi testuali delle entità selezionate per le chiamate API
            const names = {};

            if (this.selectedClient) {
                const client = this.availableClients.find(c => c.value === this.selectedClient);
                if (client) names.client = client.text;
            }

            if (this.selectedBuilding) {
                const building = this.availableBuildings.find(b => b.value === this.selectedBuilding);
                if (building) names.building = building.text;
            }

            if (this.selectedFloor) {
                const floor = this.availableFloors.find(f => f.value === this.selectedFloor);
                if (floor) names.floor = floor.text;
            }

            if (this.selectedArea) {
                const area = this.availableAreas.find(a => a.value === this.selectedArea);
                if (area) names.area = area.text;
            }

            // Per le selezioni multiple, salva array di nomi
            if (this.selectedBuildings && this.selectedBuildings.length > 0) {
                names.buildings = this.selectedBuildings.map(id => {
                    const building = this.availableBuildings.find(b => b.value === id);
                    return building ? building.text : null;
                }).filter(name => name !== null);
            }

            if (this.selectedFloors && this.selectedFloors.length > 0) {
                names.floors = this.selectedFloors.map(id => {
                    const floor = this.availableFloors.find(f => f.value === id);
                    return floor ? floor.text : null;
                }).filter(name => name !== null);
            }

            if (this.selectedAreas && this.selectedAreas.length > 0) {
                names.areas = this.selectedAreas.map(id => {
                    const area = this.availableAreas.find(a => a.value === id);
                    return area ? area.text : null;
                }).filter(name => name !== null);
            }

            return names;
        },

        getSelectedEntityInfo() {
            const clientNames = [];
            const details = [];
            const counts = {
                clients: 0,
                buildings: 0,
                floors: 0,
                areas: 0,
                sensors: 0
            };

            // Calcola le informazioni basate sulle selezioni effettive
            switch (this.selectedDataLevel) {
                case 'client': {
                    counts.clients = this.selectedClients?.length || 0;
                    if (counts.clients > 0) {
                        this.selectedClients.forEach(clientId => {
                            const client = this.availableClients.find(c => c.value === clientId);
                            if (client) clientNames.push(client.text);
                        });
                        details.push(`Clients: ${clientNames.join(', ')}`);
                    }
                    break;
                }
                case 'building': {
                    counts.buildings = this.selectedBuildings?.length || 0;
                    
                    // Get client name (single selection)
                    if (this.selectedClient) {
                        const client = this.availableClients.find(c => c.value === this.selectedClient);
                        if (client) {
                            clientNames.push(client.text);
                            counts.clients = 1;
                        }
                    }
                    
                    // Get building names (multiple selection)
                    const buildingNames = [];
                    this.selectedBuildings.forEach(buildingId => {
                        const building = this.availableBuildings.find(b => b.value === buildingId);
                        if (building) buildingNames.push(building.text);
                    });
                    
                    if (clientNames.length > 0) details.push(`Client: ${clientNames[0]}`);
                    if (buildingNames.length > 0) details.push(`Buildings: ${buildingNames.join(', ')}`);
                    break;
                }
                case 'floor': {
                    counts.floors = this.selectedFloors?.length || 0;
                    
                    // Get hierarchy info (all single selections until floors)
                    if (this.selectedClient) {
                        const client = this.availableClients.find(c => c.value === this.selectedClient);
                        if (client) {
                            clientNames.push(client.text);
                            counts.clients = 1;
                        }
                    }
                    
                    let buildingName = '';
                    if (this.selectedBuilding) {
                        const building = this.availableBuildings.find(b => b.value === this.selectedBuilding);
                        if (building) {
                            buildingName = building.text;
                            counts.buildings = 1;
                        }
                    }
                    
                    // Get floor names (multiple selection)
                    const floorNames = [];
                    this.selectedFloors.forEach(floorId => {
                        const floor = this.availableFloors.find(f => f.value === floorId);
                        if (floor) floorNames.push(floor.text);
                    });
                    
                    if (clientNames.length > 0) details.push(`Client: ${clientNames[0]}`);
                    if (buildingName) details.push(`Building: ${buildingName}`);
                    if (floorNames.length > 0) details.push(`Floors: ${floorNames.join(', ')}`);
                    break;
                }
                case 'area': {
                    counts.areas = this.selectedAreas?.length || 0;
                    
                    // Get hierarchy info (all single selections until areas)
                    if (this.selectedClient) {
                        const client = this.availableClients.find(c => c.value === this.selectedClient);
                        if (client) {
                            clientNames.push(client.text);
                            counts.clients = 1;
                        }
                    }
                    
                    let buildingName = '';
                    if (this.selectedBuilding) {
                        const building = this.availableBuildings.find(b => b.value === this.selectedBuilding);
                        if (building) buildingName = building.text;
                    }
                    
                    let floorName = '';
                    if (this.selectedFloor) {
                        const floor = this.availableFloors.find(f => f.value === this.selectedFloor);
                        if (floor) floorName = floor.text;
                    }
                    
                    // Get area names (multiple selection)
                    const areaNames = [];
                    this.selectedAreas.forEach(areaId => {
                        const area = this.availableAreas.find(a => a.value === areaId);
                        if (area) areaNames.push(area.text);
                    });
                    
                    if (clientNames.length > 0) details.push(`Client: ${clientNames[0]}`);
                    if (buildingName) details.push(`Building: ${buildingName}`);
                    if (floorName) details.push(`Floor: ${floorName}`);
                    if (areaNames.length > 0) details.push(`Areas: ${areaNames.join(', ')}`);
                    break;
                }
                case 'sensor': {
                    counts.sensors = this.selectedSensors?.length || 0;
                    
                    // Get hierarchy info (all single selections until sensors)
                    if (this.selectedClient) {
                        const client = this.availableClients.find(c => c.value === this.selectedClient);
                        if (client) {
                            clientNames.push(client.text);
                            counts.clients = 1;
                        }
                    }
                    
                    let buildingName = '';
                    if (this.selectedBuilding) {
                        const building = this.availableBuildings.find(b => b.value === this.selectedBuilding);
                        if (building) buildingName = building.text;
                    }
                    
                    let floorName = '';
                    if (this.selectedFloor) {
                        const floor = this.availableFloors.find(f => f.value === this.selectedFloor);
                        if (floor) floorName = floor.text;
                    }
                    
                    let areaName = '';
                    if (this.selectedArea) {
                        const area = this.availableAreas.find(a => a.value === this.selectedArea);
                        if (area) areaName = area.text;
                    }
                    
                    // Get sensor names (multiple selection)
                    const sensorNames = [];
                    this.selectedSensors.forEach(sensorId => {
                        const sensor = this.availableSensors.find(s => s.value === sensorId);
                        if (sensor) sensorNames.push(sensor.text);
                    });
                    
                    if (clientNames.length > 0) details.push(`Client: ${clientNames[0]}`);
                    if (buildingName) details.push(`Building: ${buildingName}`);
                    if (floorName) details.push(`Floor: ${floorName}`);
                    if (areaName) details.push(`Area: ${areaName}`);
                    if (sensorNames.length > 0) details.push(`Sensors: ${sensorNames.join(', ')}`);
                    break;
                }
            }

            // Calcola clientInfo e summary intelligenti
            let clientInfo = '';
            let summary = '';

            if (counts.clients === 1) {
                clientInfo = clientNames[0] || '';
            } else if (counts.clients > 1) {
                clientInfo = `${counts.clients} Clients selected`;
            }

            // Crea summary basato sui conteggi
            const summaryParts = [];
            if (counts.clients > 1) summaryParts.push(`${counts.clients} Clients`);
            if (counts.buildings > 0) summaryParts.push(`${counts.buildings} Buildings`);
            if (counts.floors > 0) summaryParts.push(`${counts.floors} Floors`);
            if (counts.areas > 0) summaryParts.push(`${counts.areas} Areas`);
            if (counts.sensors > 0) summaryParts.push(`${counts.sensors} Sensors`);
            
            summary = summaryParts.join(' - ') + ' selected';

            return {
                clientInfo,
                summary,
                tooltipDetails: details.join('\n') || 'Configurazione widget KPI'
            };
        },

        getSelectedEntityId() {
            switch (this.selectedDataLevel) {
                case 'client': return this.selectedClients; // Array - multiple clients
                case 'building': return this.selectedBuildings; // Array - multiple buildings
                case 'floor': return this.selectedFloors; // Array - multiple floors
                case 'area': return this.selectedAreas; // Array - multiple areas
                case 'sensor': return this.selectedSensors; // Array - multiple sensors
                default: return null;
            }
        },

        isNumericKpi(kpiType) {
            const numericKpis = [
                'refill_frequency_avg_day',
                'restroom_daily_usage_avg', 
                'alert_activity_avg_day'
            ];
            return numericKpis.includes(kpiType);
        },

        isTableKpi(kpiType) {
            const tableKpis = [
                'total_paper_consumption',
                'refill_frequency_avg_day',
                'restroom_daily_usage_avg',
                'alert_activity_avg_day'
            ];
            return tableKpis.includes(kpiType);
        },

        isChartKpi(kpiType) {
            const chartKpis = [
                'consumption_comparison',
                'usage_frequency_comparison',
                'refill_frequency_comparison',
                'heatmap_event_distribution',
                'consumable_cost_analysis'
            ];
            return chartKpis.includes(kpiType);
        },

        getTableHeaders(widget) {
            // Headers specifici per tipo di KPI
            switch (widget.kpiType) {
                case 'total_paper_consumption': {
                    // Determina se mostrare "Device" o "Article" basato sul dataLevel
                    let firstColumnText = 'Article';
                    if (widget.dataLevel && widget.dataLevel !== 'client') {
                        // Se il dataLevel è diverso da client, mostra "Device/Location"
                        firstColumnText = this.getFirstColumnName(widget.dataLevel);
                    }
                    
                    return [
                        {
                            text: firstColumnText,
                            value: 'name',
                            align: 'start',
                            sortable: true
                        },
                        {
                            text: 'Packs Used',
                            value: 'packsUsed',
                            align: 'center',
                            sortable: true
                        },
                        {
                            text: '▲ vs previous period',
                            value: 'trend',
                            align: 'center',
                            sortable: true
                        }
                    ];
                }
                
                case 'refill_frequency_avg_day':
                    return [
                        {
                            text: 'Location',
                            value: 'location',
                            align: 'start',
                            sortable: true
                        },
                        {
                            text: 'Refills / Day',
                            value: 'refillsPerDay',
                            align: 'center',
                            sortable: true
                        },
                        {
                            text: 'Δ vs prev.',
                            value: 'trend',
                            align: 'center',
                            sortable: true
                        }
                    ];
                
                case 'restroom_daily_usage_avg':
                    return [
                        {
                            text: 'Restroom',
                            value: 'restroom',
                            align: 'start',
                            sortable: true
                        },
                        {
                            text: 'Location',
                            value: 'location',
                            align: 'start',
                            sortable: true
                        },
                        {
                            text: 'Avg Entries / Day',
                            value: 'avgEntries',
                            align: 'center',
                            sortable: true
                        },
                        {
                            text: 'Status',
                            value: 'status',
                            align: 'center',
                            sortable: false
                        }
                    ];
                
                case 'alert_activity_avg_day':
                    return [
                        {
                            text: this.getFirstColumnName(widget.dataLevel),
                            value: 'name',
                            align: 'start',
                            sortable: true
                        },
                        {
                            text: 'Location',
                            value: 'location',
                            align: 'start',
                            sortable: true
                        },
                        {
                            text: 'Avg alerts / day',
                            value: 'avgAlerts',
                            align: 'center',
                            sortable: true
                        },
                        {
                            text: 'Δ vs previous period',
                            value: 'trend',
                            align: 'center',
                            sortable: true
                        }
                    ];
                
                default:
                    return [
                        {
                            text: this.getFirstColumnName(widget.dataLevel),
                            value: 'name',
                            align: 'start',
                            sortable: true
                        },
                        {
                            text: 'Value',
                            value: 'value',
                            align: 'center',
                            sortable: true
                        },
                        {
                            text: '▲ vs previous period',
                            value: 'trend',
                            align: 'center',
                            sortable: true
                        }
                    ];
            }
        },

        getDefaultSortBy(kpiType) {
            switch (kpiType) {
                case 'total_paper_consumption':
                    return 'packsUsed';
                case 'refill_frequency_avg_day':
                    return 'refillsPerDay';
                case 'restroom_daily_usage_avg':
                    return 'avgEntries';
                case 'alert_activity_avg_day':
                    return 'avgAlerts';
                default:
                    return 'value';
            }
        },

        getFirstColumnName(dataLevel) {
            const columnNames = {
                'client': 'Client',
                'building': 'Building',
                'floor': 'Floor',
                'area': 'Area',
                'sensor': 'Dispenser',
                'article': 'Article'
            };
            return columnNames[dataLevel] || 'Item';
        },

        getTrendClass(trend) {
            return {
                'trend-positive': trend > 0,
                'trend-negative': trend < 0,
                'trend-neutral': trend === 0
            };
        },

        getTrendColor(trend) {
            if (trend > 0) return '#4CAF50';
            if (trend < 0) return '#F44336';
            return '#9E9E9E';
        },

        getTrendIcon(trend) {
            if (trend > 0) return 'mdi-trending-up';
            if (trend < 0) return 'mdi-trending-down';
            return 'mdi-trending-neutral';
        },

        getStatusColor(status) {
            switch (status?.toLowerCase()) {
                case 'low':
                    return '#FFA726'; // Orange
                case 'empty':
                    return '#EF5350'; // Red
                case 'normal':
                case 'active':
                    return '#66BB6A'; // Green
                default:
                    return '#9E9E9E'; // Grey
            }
        },

        getStatusIcon(status) {
            switch (status?.toLowerCase()) {
                case 'low':
                    return 'mdi-alert-triangle';
                case 'empty':
                    return 'mdi-alert-circle';
                case 'normal':
                case 'active':
                    return 'mdi-check-circle';
                default:
                    return 'mdi-help-circle';
            }
        },

        formatDate(dateString) {
            if (!dateString) return '';
            const date = new Date(dateString);
            return date.toLocaleDateString('en-GB', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric'
            });
        },

        getChartTypeForKpi(kpiType) {
            switch (kpiType) {
                case 'consumption_comparison':
                    return 'line'; // Grafico a linee (tempo vs consumo)
                case 'refill_frequency_comparison':
                    return 'bar';
                case 'usage_frequency_comparison':
                    return 'line';
                case 'heatmap_event_distribution':
                    return 'heatmap';
                case 'consumable_cost_analysis':
                    return 'area';
                default:
                    return 'bar';
            }
        },

        getKpiChartOptions(widget) {
            const baseOptions = {
                chart: {
                    height: '100%',
                    toolbar: { show: false },
                    zoom: { enabled: false }
                },
                colors: [widget.color || '#213A8F'],
                dataLabels: { enabled: false },
                legend: { show: false },
                grid: {
                    show: true,
                    borderColor: '#e0e0e0',
                    strokeDashArray: 2,
                },
                tooltip: {
                    theme: 'light'
                }
            };

            // Configurazioni specifiche per tipo
            switch (widget.kpiType) {
                case 'heatmap_event_distribution':
                    return {
                        ...baseOptions,
                        colors: ['#1f77b4'], // Colore base per heatmap
                        dataLabels: { 
                            enabled: true,
                            style: {
                                fontSize: '10px',
                                fontWeight: 500,
                                colors: ['#fff']
                            }
                        },
                        plotOptions: {
                            heatmap: {
                                radius: 2,
                                enableShades: true,
                                shadeIntensity: 0.3,
                                reverseNegativeShade: false,
                                distributed: false,
                                useFillColorAsStroke: false,
                                colorScale: {
                                    ranges: [
                                        { 
                                            from: 0, 
                                            to: 5, 
                                            color: '#E8F4F8',
                                            name: 'Very Low' 
                                        },
                                        { 
                                            from: 6, 
                                            to: 15, 
                                            color: '#B8E0F0',
                                            name: 'Low' 
                                        },
                                        { 
                                            from: 16, 
                                            to: 25, 
                                            color: '#7BC8E2',
                                            name: 'Medium' 
                                        },
                                        { 
                                            from: 26, 
                                            to: 35, 
                                            color: '#3DA8CC',
                                            name: 'High' 
                                        },
                                        { 
                                            from: 36, 
                                            to: 50, 
                                            color: '#1E88A8',
                                            name: 'Very High' 
                                        },
                                        { 
                                            from: 51, 
                                            to: 999, 
                                            color: '#0F5F7A',
                                            name: 'Extreme' 
                                        }
                                    ]
                                }
                            }
                        },
                        xaxis: {
                            type: 'category',
                            position: 'bottom',
                            labels: {
                                style: { 
                                    fontSize: '11px',
                                    fontWeight: 500,
                                    colors: '#666'
                                },
                                rotate: -45
                            },
                            axisBorder: {
                                show: true,
                                color: '#e0e0e0'
                            },
                            axisTicks: {
                                show: true,
                                color: '#e0e0e0'
                            }
                        },
                        yaxis: {
                            labels: { 
                                style: { 
                                    fontSize: '11px',
                                    fontWeight: 500,
                                    colors: '#666'
                                }
                            }
                        },
                        grid: {
                            show: true,
                            borderColor: '#f1f1f1',
                            strokeDashArray: 0,
                            xaxis: {
                                lines: {
                                    show: true
                                }
                            },
                            yaxis: {
                                lines: {
                                    show: true
                                }
                            }
                        },
                        tooltip: {
                            theme: 'dark',
                            style: {
                                fontSize: '12px',
                                backgroundColor: 'rgba(0,0,0,0.8)',
                                color: '#fff'
                            },
                            marker: {
                                show: false
                            },
                            y: {
                                formatter: function(value) {
                                    return value + ' events';
                                },
                                title: {
                                    formatter: function(seriesName) {
                                        return '📡 ' + seriesName + ': ';
                                    }
                                }
                            },
                            x: {
                                formatter: function(value) {
                                    return '🕒 ' + value;
                                }
                            }
                        },
                        legend: {
                            show: false // Per heatmap la legenda è sostituita dalla scala colori
                        }
                    };
                case 'consumption_comparison':
                    return {
                        ...baseOptions,
                        colors: ['#213A8F', '#009CAB', '#4CAF50', '#FF9800', '#E91E63'], // Multiple colors for entities
                        legend: { 
                            show: true,
                            position: 'top',
                            fontSize: '12px'
                        },
                        stroke: {
                            curve: 'smooth',
                            width: 3
                        },
                        markers: {
                            size: 4,
                            hover: {
                                size: 6
                            }
                        },
                        xaxis: {
                            type: 'datetime',
                            labels: { 
                                style: { fontSize: '11px' },
                                datetimeFormatter: {
                                    year: 'yyyy',
                                    month: 'MMM',
                                    day: 'dd MMM',
                                    hour: 'HH:mm'
                                }
                            }
                        },
                        yaxis: {
                            title: {
                                text: 'Consumption (sheets)',
                                style: { fontSize: '12px', fontWeight: 600, color: '#666' }
                            },
                            labels: { 
                                style: { fontSize: '11px' },
                                formatter: (val) => Math.round(val)
                            }
                        },
                        tooltip: {
                            shared: true,
                            intersect: false,
                            x: {
                                format: 'dd MMM yyyy'
                            },
                            y: {
                                formatter: (val) => val + ' sheets'
                            }
                        }
                    };
                case 'usage_frequency_comparison':
                    return {
                        ...baseOptions,
                        colors: ['#009CAB', '#4CAF50', '#FF9800', '#E91E63'], // Colori per usage frequency
                        legend: { 
                            show: true,
                            position: 'top',
                            fontSize: '12px'
                        },
                        stroke: {
                            curve: 'smooth',
                            width: 2
                        },
                        markers: {
                            size: 3,
                            hover: {
                                size: 5
                            }
                        },
                        xaxis: {
                            type: 'datetime',
                            labels: { 
                                style: { fontSize: '11px' },
                                datetimeFormatter: {
                                    year: 'yyyy',
                                    month: 'MMM',
                                    day: 'dd MMM',
                                    hour: 'HH:mm'
                                }
                            }
                        },
                        yaxis: {
                            title: {
                                text: 'Usage (entries/hour)',
                                style: { fontSize: '12px', fontWeight: 600, color: '#666' }
                            },
                            labels: { 
                                style: { fontSize: '11px' },
                                formatter: (val) => Math.round(val)
                            }
                        },
                        tooltip: {
                            shared: true,
                            intersect: false,
                            x: {
                                format: 'dd MMM yyyy HH:mm'
                            },
                            y: {
                                formatter: (val) => val + ' entries/hour'
                            }
                        }
                    };
                case 'refill_frequency_comparison':
                    return {
                        ...baseOptions,
                        colors: ['#4CAF50', '#2196F3', '#FF9800', '#9C27B0'], // Colori per refill frequency
                        legend: { 
                            show: true,
                            position: 'top',
                            fontSize: '12px'
                        },
                        stroke: {
                            curve: 'smooth',
                            width: 3
                        },
                        markers: {
                            size: 4,
                            hover: {
                                size: 6
                            }
                        },
                        xaxis: {
                            type: 'datetime',
                            labels: { 
                                style: { fontSize: '11px' },
                                datetimeFormatter: {
                                    year: 'yyyy',
                                    month: 'MMM',
                                    day: 'dd MMM'
                                }
                            }
                        },
                        yaxis: {
                            title: {
                                text: 'Refills per day',
                                style: { fontSize: '12px', fontWeight: 600, color: '#666' }
                            },
                            labels: { 
                                style: { fontSize: '11px' },
                                formatter: (val) => val.toFixed(1)
                            }
                        },
                        tooltip: {
                            shared: true,
                            intersect: false,
                            x: {
                                format: 'dd MMM yyyy'
                            },
                            y: {
                                formatter: (val) => val.toFixed(1) + ' refills/day'
                            }
                        }
                    };
                case 'consumable_cost_analysis':
                    return {
                        ...baseOptions,
                        fill: {
                            type: 'gradient',
                            gradient: {
                                shadeIntensity: 1,
                                opacityFrom: 0.7,
                                opacityTo: 0.3,
                                stops: [0, 90, 100]
                            }
                        },
                        xaxis: {
                            type: 'datetime',
                            labels: { style: { fontSize: '12px' } }
                        },
                        yaxis: {
                            labels: { 
                                formatter: (val) => '€' + val.toFixed(2),
                                style: { fontSize: '12px' }
                            }
                        }
                    };
                default:
                    return {
                        ...baseOptions,
                        xaxis: {
                            labels: { style: { fontSize: '12px' } }
                        },
                                                yaxis: {
                            labels: { style: { fontSize: '12px' } }
                        }
                    };
            }
        },

        isWidgetConfigurationComplete() {
            // Check if all required fields are filled
            const hasValidDataLevel = this.selectedDataLevel;
            const hasValidPeriod = this.selectedPeriod;
            const hasValidTitle = this.widgetTitle && this.widgetTitle.trim() !== '';
            
            // For custom period, ensure both dates are selected and valid
            let hasValidCustomPeriod = true;
            if (this.selectedPeriod === 'custom') {
                const yesterday = this.getYesterday();
                const minDate = this.getMinSelectableDate();
                
                hasValidCustomPeriod = this.customStartDate && 
                                     this.customEndDate && 
                                     this.customStartDate <= this.customEndDate &&
                                     this.customStartDate >= minDate && // Non più di 1 mese fa
                                     this.customEndDate <= yesterday && // Non nel futuro
                                     this.getDateDifference(this.customStartDate, this.customEndDate) >= 1; // Almeno 1 giorno
            }
            
            // Check entity selection based on data level
            let hasValidEntitySelection = true;
            switch (this.selectedDataLevel) {
                case 'client':
                    // Multiple clients selection required
                    hasValidEntitySelection = this.selectedClients?.length > 0;
                    break;
                case 'building':
                    // Single client + multiple buildings required
                    hasValidEntitySelection = this.selectedClient && this.selectedBuildings?.length > 0;
                    break;
                case 'floor':
                    // Single client + single building + multiple floors required
                    hasValidEntitySelection = this.selectedClient && 
                                            this.selectedBuilding && 
                                            this.selectedFloors?.length > 0;
                    break;
                case 'area':
                    // Single client + single building + single floor + multiple areas required
                    hasValidEntitySelection = this.selectedClient && 
                                            this.selectedBuilding && 
                                            this.selectedFloor && 
                                            this.selectedAreas?.length > 0;
                    break;
                case 'sensor':
                    // Single selections until area + multiple sensors required
                    hasValidEntitySelection = this.selectedClient && 
                                            this.selectedBuilding && 
                                            this.selectedFloor && 
                                            this.selectedArea && 
                                            this.selectedSensors?.length > 0;
                    break;
            }

            return hasValidDataLevel && hasValidPeriod && hasValidTitle && hasValidCustomPeriod && hasValidEntitySelection;
        },
        

        async fetchChartData(widget) {
            try {
                const params = {
                    template: widget.template,
                    comparisonLevel: widget.comparisonLevel,
                    entities: widget.entities.map(e => e.value),
                    period: widget.period,
                    startDate: widget.startDate,
                    endDate: widget.endDate
                };

                const response = await axios.post(
                    this.$store.state.URL_GET_CHART_DATA,
                    params
                );

                return response.data;
            } catch (error) {
                console.error('Errore nel recupero dei dati del grafico:', error);
                return [];
            }
        },

        async fetchKpiData(widget) {
            try {
                // Chiamata API reale per Total Paper Consumption
                if (widget.kpiType === 'total_paper_consumption') {
                    return await this.fetchPaperConsumptionData(widget);
                }

                // Per altri KPI tabellari, usa dati mock (temporaneo)
                if (this.isTableKpi(widget.kpiType)) {
                    return this.generateMockTableData(widget.kpiType);
                }

                // Per KPI grafici (chart-based), genera dati mock
                if (this.isChartKpi(widget.kpiType)) {
                    return this.generateMockChartData(widget);
                }

                const response = await axios.post(
                    this.$store.state.URL_GET_KPI_DATA,
                    {
                        type: widget.kpiType,
                        level: widget.dataLevel,
                        entityId: widget.entityId,
                        period: widget.period || 'last_7_days',
                        startDate: widget.startDate,
                        endDate: widget.endDate
                    }
                );

                const data = response.data;
                return {
                    value: this.formatKpiValue(data.value, widget.kpiType),
                    trend: data.trend,
                    previousValue: data.previousValue,
                    label: this.getKpiLabel(widget.kpiType),
                    format: this.getKpiFormat(widget.kpiType)
                };
            } catch (error) {
                console.error('Errore nel recupero dei dati KPI:', error);
                if (this.isTableKpi(widget.kpiType)) {
                    return this.generateMockTableData();
                }
                if (this.isChartKpi(widget.kpiType)) {
                    return this.generateMockChartData(widget);
                }
                return {
                    value: 0,
                    trend: 0,
                    previousValue: 0,
                    label: this.getKpiLabel(widget.kpiType),
                    format: this.getKpiFormat(widget.kpiType)
                };
            }
        },

        async fetchPaperConsumptionData(widget) {
            try {
                // Costruisci il payload per l'API
                const payload = this.buildPaperConsumptionPayload(widget);
                
                if (!payload) {
                    console.warn('Impossibile costruire il payload per paper consumption');
                    return this.generateMockTableData('total_paper_consumption');
                }

                // Chiamata API GET con parametri come query string
                const baseUrl = this.$store.state.BASE_PATH;
                const endpoint = this.$store.state.URL_GET_PAPER_CONSUMPTION;
                const fullUrl = baseUrl + endpoint;

                const response = await axios.get(fullUrl, { params: payload });
                
                // Mappa la risposta alla struttura tabellare
                const apiData = response.data;
                const periodInfo = this.getSelectedPeriodInfo();
                
                // Crea le righe della tabella dai dispositivi
                const tableData = apiData.data.deviceBreakdown.map(device => ({
                    name: device.location || device.deviceId,
                    packsUsed: device.paperPacks || 0,
                    trend: device.percentage || 0
                }));

                // Ordina per packsUsed decrescente
                tableData.sort((a, b) => b.packsUsed - a.packsUsed);

                return {
                    tableData: tableData,
                    period: apiData.period || this.getPeriodDisplayText(),
                    periodEnd: periodInfo ? periodInfo.endDate : this.getYesterday(),
                    totalPacks: apiData.data.totalPaperPacks,
                    avgPacksPerDay: apiData.data.avgPacksPerDay,
                    percentageChange: apiData.data.percentageChange,
                    trend: apiData.data.trend
                };
            } catch (error) {
                console.error('Errore nel recupero dei dati Paper Consumption:', error);
                // Fallback a dati mock in caso di errore
                return this.generateMockTableData('total_paper_consumption');
            }
        },

        buildPaperConsumptionPayload(widget) {
            // Costruisce il payload basato sul data level e sulle selezioni
            const payload = {};

            // Costruisci locationFilter come oggetto
            const locationFilter = {};

            // Usa i nomi salvati nel widget (entityNames) se disponibili
            if (widget.entityNames) {
                if (widget.entityNames.building) {
                    locationFilter.building = widget.entityNames.building;
                }
                if (widget.entityNames.floor) {
                    locationFilter.floor = widget.entityNames.floor;
                }
                if (widget.entityNames.area) {
                    locationFilter.area = widget.entityNames.area;
                }
                
                // Se ci sono multiple selezioni, prendi la prima per la chiamata API
                if (!locationFilter.building && widget.entityNames.buildings && widget.entityNames.buildings.length > 0) {
                    locationFilter.building = widget.entityNames.buildings[0];
                }
                if (!locationFilter.floor && widget.entityNames.floors && widget.entityNames.floors.length > 0) {
                    locationFilter.floor = widget.entityNames.floors[0];
                }
                if (!locationFilter.area && widget.entityNames.areas && widget.entityNames.areas.length > 0) {
                    locationFilter.area = widget.entityNames.areas[0];
                }
            }

            // Verifica che locationFilter abbia almeno un campo valido
            if (!locationFilter.building && !locationFilter.floor && !locationFilter.area) {
                console.warn('Nessuna entità valida trovata per costruire il locationFilter');
                return null;
            }

            // Aggiungi locationFilter al payload
            payload.locationFilter = locationFilter;

            // Aggiungi informazioni sul periodo (timePeriod invece di period)
            if (widget.periodType) {
                payload.timePeriod = widget.periodType;
                // Aggiungi startDate e endDate solo se il periodo è 'custom'
                if (widget.periodType === 'custom' && widget.periodStartDate && widget.periodEndDate) {
                    payload.startDate = widget.periodStartDate;
                    payload.endDate = widget.periodEndDate;
                }
            } else {
                // Default a last_30_days se non specificato
                payload.timePeriod = 'last_30_days';
            }

            console.log('Payload costruito per Paper Consumption API:', payload);
            return payload;
        },

        generateMockTableData(kpiType = null) {
            // Use selected period info if available
            const periodInfo = this.getSelectedPeriodInfo();
            const periodText = this.selectedPeriod ? this.getPeriodDisplayText() : 'Last 30 days';
            const periodEnd = periodInfo ? periodInfo.endDate : this.getYesterday();

            // Usa il parametro kpiType passato o fallback su selectedKpiType
            const currentKpiType = kpiType || this.selectedKpiType;

            // Genera dati mock realistici per Total Paper Consumption
            if (currentKpiType === 'total_paper_consumption') {
                return this.generatePaperConsumptionMockData(periodText, periodEnd);
            }

            // Genera dati mock per Refill Frequency - Avg / Day
            if (currentKpiType === 'refill_frequency_avg_day') {
                return this.generateRefillFrequencyMockData(periodText, periodEnd);
            }

            // Genera dati mock per Restroom Daily Usage - Avg / Day
            if (currentKpiType === 'restroom_daily_usage_avg') {
                return this.generateRestroomUsageMockData(periodText, periodEnd);
            }

            // Genera dati mock per Alert Activity - Avg / Day
            if (currentKpiType === 'alert_activity_avg_day') {
                return this.generateAlertActivityMockData(periodText, periodEnd);
            }

            // Fallback per altri KPI
            return {
                tableData: [],
                period: periodText,
                periodEnd: periodEnd
            };
        },

        generateRefillFrequencyMockData(periodText, periodEnd) {
            // Determina il livello di visualizzazione in base alle selezioni
            let mockData = [];
            
            // LIVELLO AGGREGATO: quando si selezionano multiple entità del livello più alto
            if (this.isAggregatedLevel()) {
                mockData = [
                    { location: 'Client A > Bldg 1 > Floor 2', refillsPerDay: 1.2, trend: 5.1 },
                    { location: 'Client A > Bldg 1 > Floor 3', refillsPerDay: 0.8, trend: -2.0 },
                    { location: 'Client A > Bldg 2 > Floor 1', refillsPerDay: 1.6, trend: 1.3 }
                ];
            }
            // LIVELLO AREA: quando si arriva fino alle aree
            else if (this.selectedDataLevel === 'area' || this.isAreaLevel()) {
                mockData = [
                    { location: 'Area A', refillsPerDay: 1.2, trend: 5.1 },
                    { location: 'Area B', refillsPerDay: 0.8, trend: -2.0 },
                    { location: 'Area C', refillsPerDay: 1.6, trend: 1.3 }
                ];
            }
            // LIVELLO SENSOR: quando si arriva fino ai sensori/dispenser
            else if (this.selectedDataLevel === 'sensor' || this.isSensorLevel()) {
                mockData = [
                    { location: 'Client A > Bldg 1 > Floor 2 > Area', refillsPerDay: 1.2, trend: 0.9 },
                    { location: 'Client A > Bldg 1 > Floor 3 > Area', refillsPerDay: 0.8, trend: -1.1 },
                    { location: 'Client A > Bldg 2 > Floor 1 > Area', refillsPerDay: 1.6, trend: 2.4 }
                ];
            }
            // DEFAULT: Livello aggregato generico
            else {
                mockData = [
                    { location: 'Client A > Bldg 1 > Floor 2', refillsPerDay: 1.2, trend: 5.1 },
                    { location: 'Client A > Bldg 1 > Floor 3', refillsPerDay: 0.8, trend: -2.0 },
                    { location: 'Client A > Bldg 2 > Floor 1', refillsPerDay: 1.6, trend: 1.3 }
                ];
            }

            return {
                tableData: mockData,
                period: periodText,
                periodEnd: periodEnd
            };
        },

        generateRestroomUsageMockData(periodText, periodEnd) {
            // Dati mock realistici per Restroom Daily Usage basati sull'immagine
            let mockData = [];
            
            // Scenario base come mostrato nell'immagine
            if (this.selectedDataLevel === 'building' || this.isAggregatedLevel()) {
                mockData = [
                    { 
                        restroom: 'WC-N1', 
                        location: 'Client A > Building 1 > Floor 2 > Area B',
                        avgEntries: 1.3,
                        status: 'Low'
                    },
                    { 
                        restroom: 'WC-West-Lobby', 
                        location: 'Client A > Building 1 > Floor 1 > Area A',
                        avgEntries: 0.0,
                        status: 'Empty'
                    },
                    { 
                        restroom: 'WC-Showroom', 
                        location: 'Client A > Building 2 > Floor 3 > Show',
                        avgEntries: 2.0,
                        status: 'Low'
                    },
                    { 
                        restroom: 'WC-Lobby-F1', 
                        location: 'Client A > Building 1 > Floor 1 > Lobby',
                        avgEntries: 8.7,
                        status: 'Active'
                    }
                ];
            }
            // Altri scenari possono essere aggiunti qui per diversi data levels
            else {
                mockData = [
                    { 
                        restroom: 'WC-Main', 
                        location: 'Ground Floor > Reception',
                        avgEntries: 12.5,
                        status: 'Active'
                    },
                    { 
                        restroom: 'WC-Staff', 
                        location: 'First Floor > Office',
                        avgEntries: 8.2,
                        status: 'Active'
                    },
                    { 
                        restroom: 'WC-Public', 
                        location: 'Ground Floor > Lobby',
                        avgEntries: 0.5,
                        status: 'Low'
                    }
                ];
            }

            return {
                tableData: mockData,
                period: periodText,
                periodEnd: periodEnd
            };
        },

        generateAlertActivityMockData(periodText, periodEnd) {
            // Dati mock realistici per Alert Activity basati sull'immagine
            let mockData = [];
            
            // Scenario per diversi data levels
            switch (this.selectedDataLevel) {
                case 'floor':
                    // Scenario come mostrato nell'immagine (floor level)
                    mockData = [
                        { 
                            name: 'Floor 3', 
                            location: 'Client A > Building 1',
                            avgAlerts: 38,
                            trend: 6.0
                        },
                        { 
                            name: 'Floor 2', 
                            location: 'Client A > Building 1',
                            avgAlerts: 22,
                            trend: -3.0
                        },
                        { 
                            name: 'Floor 1', 
                            location: 'Client B > Building 2',
                            avgAlerts: 15,
                            trend: 0.0
                        },
                        { 
                            name: 'Floor -1', 
                            location: 'Client A > Building 1',
                            avgAlerts: 11,
                            trend: 2.0
                        }
                    ];
                    break;
                    
                case 'building':
                    mockData = [
                        { 
                            name: 'Building Alpha', 
                            location: 'Client A > North Campus',
                            avgAlerts: 45,
                            trend: 8.5
                        },
                        { 
                            name: 'Building Beta', 
                            location: 'Client A > South Campus',
                            avgAlerts: 32,
                            trend: -2.1
                        },
                        { 
                            name: 'Building Gamma', 
                            location: 'Client B > Main Site',
                            avgAlerts: 28,
                            trend: 4.3
                        }
                    ];
                    break;
                    
                case 'area':
                    mockData = [
                        { 
                            name: 'Reception Area', 
                            location: 'Client A > Building 1 > Floor 1',
                            avgAlerts: 12,
                            trend: 3.2
                        },
                        { 
                            name: 'Office Zone A', 
                            location: 'Client A > Building 1 > Floor 2',
                            avgAlerts: 18,
                            trend: -1.5
                        },
                        { 
                            name: 'Meeting Rooms', 
                            location: 'Client A > Building 1 > Floor 3',
                            avgAlerts: 9,
                            trend: 0.8
                        },
                        { 
                            name: 'Cafeteria', 
                            location: 'Client A > Building 1 > Floor 1',
                            avgAlerts: 25,
                            trend: 5.7
                        }
                    ];
                    break;
                    
                case 'sensor':
                    mockData = [
                        { 
                            name: 'DISP-137', 
                            location: 'Client A > Building 1 > Floor 2 > Area A',
                            avgAlerts: 8,
                            trend: 2.1
                        },
                        { 
                            name: 'DISP-143', 
                            location: 'Client A > Building 1 > Floor 2 > Area B',
                            avgAlerts: 14,
                            trend: -4.2
                        },
                        { 
                            name: 'DISP-166', 
                            location: 'Client A > Building 1 > Floor 3 > Area C',
                            avgAlerts: 6,
                            trend: 1.8
                        },
                        { 
                            name: 'DISP-201', 
                            location: 'Client B > Building 2 > Floor 1 > Area A',
                            avgAlerts: 19,
                            trend: 7.3
                        }
                    ];
                    break;
                    
                default:
                    // Fallback generico (client level)
                    mockData = [
                        { 
                            name: 'Client Alpha', 
                            location: 'Multiple Buildings',
                            avgAlerts: 156,
                            trend: 4.8
                        },
                        { 
                            name: 'Client Beta', 
                            location: 'Multiple Buildings',
                            avgAlerts: 89,
                            trend: -1.2
                        },
                        { 
                            name: 'Client Gamma', 
                            location: 'Multiple Buildings',
                            avgAlerts: 234,
                            trend: 12.5
                        }
                    ];
            }

            return {
                tableData: mockData,
                period: periodText,
                periodEnd: periodEnd
            };
        },

        generateMockChartData(widget) {
            const periodInfo = this.getSelectedPeriodInfo();
            
            switch (widget.kpiType) {
                case 'consumption_comparison':
                    return this.generateConsumptionComparisonData(periodInfo);
                case 'usage_frequency_comparison':
                    return this.generateUsageFrequencyComparisonData(periodInfo);
                case 'refill_frequency_comparison':
                    return this.generateRefillFrequencyComparisonData(periodInfo);
                case 'heatmap_event_distribution':
                    return this.generateHeatmapEventDistributionData(periodInfo);
                default:
                    return {
                        series: [],
                        chartData: {}
                    };
            }
        },

        generateConsumptionComparisonData(periodInfo) {
            // Genera dati temporali per il confronto dei consumi
            const days = periodInfo ? periodInfo.days : 7;
            const endDate = periodInfo ? new Date(periodInfo.endDate) : new Date();
            const startDate = new Date(endDate);
            startDate.setDate(startDate.getDate() - days + 1);

            // Genera date per l'asse X
            const dates = [];
            const currentDate = new Date(startDate);
            while (currentDate <= endDate) {
                dates.push(new Date(currentDate).getTime());
                currentDate.setDate(currentDate.getDate() + 1);
            }

            // Genera serie di dati per diverse entità basate sul data level
            const series = this.generateSeriesForDataLevel(dates);

            return {
                series: series,
                chartData: {
                    categories: dates,
                    period: this.getPeriodDisplayText()
                }
            };
        },

                generateSeriesForDataLevel(dates) {
            const baseConsumption = 150; // Consumo base giornaliero
            
            // Utilizza helper method per consistenza
            const entities = this.getEntitiesForDataLevel();

            return entities.map((entityName, index) => {
                const multiplier = 0.7 + (index * 0.4); // Variazione tra entità
                const variance = 0.3; // Variabilità giornaliera

                const data = dates.map(date => {
                    // Aggiungi variabilità giornaliera e trend
                    const dayOffset = Math.sin((date / (1000 * 60 * 60 * 24)) * 0.1) * 20;
                    const randomVariation = (Math.random() - 0.5) * variance * baseConsumption;
                    const consumption = Math.round((baseConsumption * multiplier) + dayOffset + randomVariation);
                    
                    return [date, Math.max(0, consumption)];
                });

                return {
                    name: entityName,
                    data: data,
                    type: 'line'
                };
            });
        },

        generateUsageFrequencyComparisonData(periodInfo) {
            // Genera dati temporali per il confronto della frequenza d'uso (tempo vs ingressi)
            const days = periodInfo ? periodInfo.days : 7;
            const endDate = periodInfo ? new Date(periodInfo.endDate) : new Date();
            const startDate = new Date(endDate);
            startDate.setDate(startDate.getDate() - days + 1);

            // Per usage frequency, generiamo dati orari per mostrare pattern comportamentali
            const timePoints = [];
            const currentTime = new Date(startDate);
            
            // Generiamo punti dati ogni 2 ore per il periodo selezionato
            while (currentTime <= endDate) {
                for (let hour = 8; hour <= 18; hour += 2) { // Ore lavorative 8-18 ogni 2 ore
                    const timePoint = new Date(currentTime);
                    timePoint.setHours(hour, 0, 0, 0);
                    if (timePoint <= endDate) {
                        timePoints.push(timePoint.getTime());
                    }
                }
                currentTime.setDate(currentTime.getDate() + 1);
            }

            // Genera serie per diverse entità
            const entities = this.getEntitiesForDataLevel();
            const series = entities.map((entityName, index) => {
                const baseUsage = 15; // Uso base per fascia oraria
                const peakMultiplier = 1.5 + (index * 0.2); // Variazione tra entità
                
                const data = timePoints.map(timestamp => {
                    const hour = new Date(timestamp).getHours();
                    
                    // Pattern di utilizzo realistico: picchi a metà mattina e pomeriggio
                    let hourlyMultiplier = 1;
                    if (hour >= 10 && hour <= 11) hourlyMultiplier = peakMultiplier; // Picco mattutino
                    else if (hour >= 14 && hour <= 15) hourlyMultiplier = peakMultiplier * 0.8; // Picco pomeridiano
                    else if (hour <= 9 || hour >= 17) hourlyMultiplier = 0.3; // Basso utilizzo inizio/fine giornata
                    else hourlyMultiplier = 0.7; // Utilizzo normale
                    
                    // Aggiungi variabilità giornaliera
                    const dayOfWeek = new Date(timestamp).getDay();
                    const weekendReduction = (dayOfWeek === 0 || dayOfWeek === 6) ? 0.3 : 1;
                    
                    const randomVariation = 0.8 + (Math.random() * 0.4); // ±20% variabilità
                    const usage = Math.round(baseUsage * hourlyMultiplier * weekendReduction * randomVariation);
                    
                    return [timestamp, Math.max(0, usage)];
                });

                return {
                    name: entityName,
                    data: data,
                    type: 'line'
                };
            });

            return {
                series: series,
                chartData: {
                    categories: timePoints,
                    period: this.getPeriodDisplayText()
                }
            };
        },

        generateRefillFrequencyComparisonData(periodInfo) {
            // Genera dati temporali per il confronto della frequenza di ricariche
            const days = periodInfo ? periodInfo.days : 7;
            const endDate = periodInfo ? new Date(periodInfo.endDate) : new Date();
            const startDate = new Date(endDate);
            startDate.setDate(startDate.getDate() - days + 1);

            // Generiamo punti dati giornalieri per le ricariche
            const dates = [];
            const currentDate = new Date(startDate);
            while (currentDate <= endDate) {
                dates.push(new Date(currentDate).getTime());
                currentDate.setDate(currentDate.getDate() + 1);
            }

            // Genera serie per diverse entità
            const entities = this.getEntitiesForDataLevel();
            const series = entities.map((entityName, index) => {
                const baseRefills = 2.5; // Ricariche base giornaliere
                const entityVariation = 0.8 + (index * 0.6); // Variazione tra entità (0.8x - 2.0x)
                
                const data = dates.map((timestamp, dayIndex) => {
                    // Pattern settimanale: più ricariche a inizio settimana
                    const dayOfWeek = new Date(timestamp).getDay();
                    let weeklyMultiplier = 1;
                    if (dayOfWeek === 1 || dayOfWeek === 2) weeklyMultiplier = 1.4; // Lunedì-Martedì
                    else if (dayOfWeek === 5 || dayOfWeek === 6) weeklyMultiplier = 0.6; // Venerdì-Sabato
                    else if (dayOfWeek === 0) weeklyMultiplier = 0.2; // Domenica
                    
                    // Trend graduale nel tempo (leggero aumento o diminuzione)
                    const trendFactor = 1 + ((dayIndex - days/2) * 0.02 * (Math.random() - 0.5));
                    
                    // Variabilità giornaliera
                    const randomVariation = 0.7 + (Math.random() * 0.6); // ±30% variabilità
                    
                    const refills = baseRefills * entityVariation * weeklyMultiplier * trendFactor * randomVariation;
                    
                    return [timestamp, Math.max(0, Math.round(refills * 10) / 10)]; // Arrotonda a 1 decimale
                });

                return {
                    name: entityName,
                    data: data,
                    type: 'line'
                };
            });

            return {
                series: series,
                chartData: {
                    categories: dates,
                    period: this.getPeriodDisplayText()
                }
            };
        },

        getEntitiesForDataLevel() {
            // Helper method per ottenere nomi di entità appropriate al data level
            switch (this.selectedDataLevel) {
                case 'building':
                    return ['Building Alpha', 'Building Beta', 'Building Gamma'];
                case 'floor':
                    return ['Floor 1', 'Floor 2', 'Floor 3', 'Floor -1'];
                case 'area':
                    return ['Reception Area', 'Office Zone A', 'Office Zone B', 'Meeting Rooms'];
                case 'sensor':
                    return ['DISP-137', 'DISP-143', 'DISP-166', 'DISP-201'];
                default:
                    return ['Client A', 'Client B', 'Client C'];
            }
                 },

        generateHeatmapEventDistributionData(periodInfo) {
            // Genera dati per heatmap distribuzione eventi nel tempo
            const days = periodInfo ? periodInfo.days : 7;
            const endDate = periodInfo ? new Date(periodInfo.endDate) : new Date();
            
            // Determina la granularità temporale
            const useHourlyData = days <= 3; // Se periodo <= 3 giorni, usa dati orari
            
            if (useHourlyData) {
                return this.generateHourlyHeatmapData();
            } else {
                return this.generateDailyHeatmapData(endDate, days);
            }
        },

        generateHourlyHeatmapData() {
            // Heatmap oraria: Y = Sensori, X = Ore del giorno (0-23)
            const sensors = this.getHeatmapSensors();
            const hours = Array.from({length: 24}, (_, i) => i); // 0-23
            
            // Simula diversi tipi di eventi
            const eventTypes = ['Usage', 'Alerts', 'Refills'];
            const selectedEventType = eventTypes[Math.floor(Math.random() * eventTypes.length)];
            
            const series = sensors.map(sensorName => {
                const data = hours.map(hour => {
                    let intensity = this.getEventIntensityByHour(hour, selectedEventType);
                    
                    // Aggiungi variabilità per sensore
                    const sensorVariation = 0.7 + (Math.random() * 0.6); // 0.7x - 1.3x
                    intensity = Math.round(intensity * sensorVariation);
                    
                    return {
                        x: hour.toString().padStart(2, '0') + ':00',
                        y: Math.max(0, intensity)
                    };
                });

                return {
                    name: sensorName,
                    data: data
                };
            });

            return {
                series: series,
                chartData: {
                    eventType: selectedEventType,
                    period: this.getPeriodDisplayText(),
                    granularity: 'hourly'
                }
            };
        },

        generateDailyHeatmapData(endDate, days) {
            // Heatmap giornaliera: Y = Sensori, X = Giorni della settimana/Date
            const sensors = this.getHeatmapSensors();
            
            // Genera date per il periodo
            const dates = [];
            const currentDate = new Date(endDate);
            currentDate.setDate(currentDate.getDate() - days + 1);
            
            for (let i = 0; i < days; i++) {
                const date = new Date(currentDate);
                date.setDate(date.getDate() + i);
                dates.push({
                    date: new Date(date),
                    label: date.getDate().toString().padStart(2, '0') + '/' + (date.getMonth() + 1).toString().padStart(2, '0')
                });
            }

            // Simula diversi tipi di eventi
            const eventTypes = ['Usage', 'Alerts', 'Refills'];
            const selectedEventType = eventTypes[Math.floor(Math.random() * eventTypes.length)];

            const series = sensors.map(sensorName => {
                const data = dates.map(dateInfo => {
                    let intensity = this.getEventIntensityByDay(dateInfo.date, selectedEventType);
                    
                    // Aggiungi variabilità per sensore
                    const sensorVariation = 0.6 + (Math.random() * 0.8); // 0.6x - 1.4x
                    intensity = Math.round(intensity * sensorVariation);
                    
                    return {
                        x: dateInfo.label,
                        y: Math.max(0, intensity)
                    };
                });

                return {
                    name: sensorName,
                    data: data
                };
            });

            return {
                series: series,
                chartData: {
                    eventType: selectedEventType,
                    period: this.getPeriodDisplayText(),
                    granularity: 'daily'
                }
            };
        },

        getHeatmapSensors() {
            // Genera sensori appropriati per heatmap (massimo 10 come da specifica)
            const allSensors = [
                'DISP-137', 'DISP-143', 'DISP-166', 'DISP-201', 'DISP-205', 
                'DISP-208', 'DISP-301', 'DISP-315', 'DISP-402', 'DISP-501'
            ];
            
            // Per demo, usa 6-8 sensori casualmente
            const sensorCount = Math.min(6 + Math.floor(Math.random() * 3), allSensors.length);
            return allSensors.slice(0, sensorCount);
        },

        getEventIntensityByHour(hour, eventType) {
            // Pattern realistici per ora del giorno
            switch (eventType) {
                case 'Usage':
                    // Picchi durante orari lavorativi
                    if (hour >= 8 && hour <= 18) {
                        if (hour === 10 || hour === 14) return 25 + Math.floor(Math.random() * 15); // Picchi
                        if (hour >= 9 && hour <= 17) return 15 + Math.floor(Math.random() * 10); // Normale
                        return 5 + Math.floor(Math.random() * 5); // Basso
                    }
                    return Math.floor(Math.random() * 3); // Notte
                    
                case 'Alerts': {
                    // Più casuali, ma leggermente più frequenti durante il giorno
                    const baseAlert = hour >= 7 && hour <= 19 ? 8 : 3;
                    return baseAlert + Math.floor(Math.random() * 8);
                }
                    
                case 'Refills':
                    // Concentrati in orari di manutenzione
                    if (hour >= 7 && hour <= 9) return 3 + Math.floor(Math.random() * 4); // Mattino
                    if (hour >= 17 && hour <= 19) return 2 + Math.floor(Math.random() * 3); // Sera
                    return Math.floor(Math.random() * 2); // Altri orari
                    
                default:
                    return Math.floor(Math.random() * 10);
            }
        },

        getEventIntensityByDay(date, eventType) {
            const dayOfWeek = date.getDay(); // 0 = Domenica
            
            switch (eventType) {
                case 'Usage':
                    // Meno usage durante weekend
                    if (dayOfWeek === 0 || dayOfWeek === 6) return 20 + Math.floor(Math.random() * 15); // Weekend
                    return 45 + Math.floor(Math.random() * 25); // Settimana
                    
                case 'Alerts': {
                    // Abbastanza costanti, leggermente meno nel weekend
                    const baseAlert = (dayOfWeek === 0 || dayOfWeek === 6) ? 15 : 25;
                    return baseAlert + Math.floor(Math.random() * 20);
                }
                    
                case 'Refills':
                    // Più refill a inizio settimana
                    if (dayOfWeek === 1 || dayOfWeek === 2) return 8 + Math.floor(Math.random() * 6); // Lun-Mar
                    if (dayOfWeek === 0) return 1 + Math.floor(Math.random() * 2); // Domenica
                    return 3 + Math.floor(Math.random() * 4); // Altri giorni
                    
                default:
                    return Math.floor(Math.random() * 30);
            }
        },

        // Metodi helper per determinare il livello di visualizzazione
        isAggregatedLevel() {
            // Livello aggregato quando si selezionano multipli client, building, o floor
            return (this.selectedDataLevel === 'client' && this.selectedClients?.length >= 1) ||
                   (this.selectedDataLevel === 'building' && this.selectedBuildings?.length >= 1) ||
                   (this.selectedDataLevel === 'floor' && this.selectedFloors?.length >= 1);
        },

        isAreaLevel() {
            // Livello area quando si selezionano multiple aree
            return this.selectedDataLevel === 'area' && this.selectedAreas?.length > 0;
        },

        isSensorLevel() {
            // Livello sensor quando si selezionano multipli sensori
            return this.selectedDataLevel === 'sensor' && this.selectedSensors?.length > 0;
        },

        generatePaperConsumptionMockData(periodText, periodEnd) {
            // Dati della tabella sempre uguali come nelle immagini
            const baseTableData = [
                { name: 'Art. 924', packsUsed: 1020, trend: 8.2 },
                { name: 'Art. 928', packsUsed: 640, trend: -3.5 },
                { name: 'Art. 911', packsUsed: 230, trend: 11.1 }
            ];

            return {
                tableData: baseTableData,
                period: periodText, // periodText già contiene il periodo selezionato dall'utente
                periodEnd: periodEnd
            };
        },

        getPaperConsumptionEntityInfo() {
            // Genera informazioni realistic come nelle immagini basate sul data level
            const scenarios = [
                {
                    // Scenario 1: Livello Building/Floor/Sensor (come prima immagine)
                    clientInfo: 'YouTissue',
                    summary: '2 Buildings - 7 Floors - 10 Dispensers selected',
                    tooltipDetails: 'Client: YouTissue\nBuildings: YouTissue HQ, Via Roma Building\nFloors: Ground Floor, First Floor, Second Floor (YouTissue HQ), Reception, Office Level, Executive Floor, Roof Terrace (Via Roma)\nDispensers: DISP-137, DISP-143, DISP-166, DISP-201, DISP-205, DISP-208, DISP-301, DISP-315, DISP-402, DISP-501'
                },
                {
                    // Scenario 2: Livello Client (come seconda immagine)  
                    clientInfo: 'Lavazza, Autogrill, Camst',
                    summary: '3 Clients selected',
                    tooltipDetails: 'Clients: Lavazza, Autogrill, Camst'
                },
                {
                    // Scenario 3: Livello YouTissue HQ con floors (come terza immagine)
                    clientInfo: 'YouTissue',
                    summary: 'YouTissue HQ - 3 Floors selected',
                    tooltipDetails: 'Client: YouTissue\nBuilding: YouTissue HQ\nFloors: Ground Floor, First Floor, Second Floor'
                }
            ];

            // Scegli scenario basato su data level o casualmente per demo
            let selectedScenario;
            switch (this.selectedDataLevel) {
                case 'client':
                    selectedScenario = Math.random() > 0.5 ? scenarios[1] : scenarios[0];
                    break;
                case 'building':
                case 'floor': 
                    selectedScenario = scenarios[2];
                    break;
                case 'sensor':
                    selectedScenario = scenarios[0];
                    break;
                default:
                    selectedScenario = scenarios[Math.floor(Math.random() * scenarios.length)];
            }

            return selectedScenario;
        },

        formatKpiValue(value, type) {
            const kpi = this.kpiTypes.find(k => k.value === type);
            switch (kpi?.format) {
                case 'sheets':
                    return Math.round(value).toLocaleString();
                case 'refills/day':
                case 'entries/day':
                case 'alerts/day':
                    return value.toFixed(1);
                case 'hours':
                    return value.toFixed(1);
                case 'percentage':
                    return value.toFixed(1) + '%';
                case 'count':
                    return Math.round(value);
                case 'currency':
                    return '€' + value.toFixed(2);
                case 'comparison':
                case 'heatmap':
                    return value; // Questi saranno gestiti come grafici
                default:
                    return value;
            }
        },

        getKpiLabel(type) {
            const kpi = this.kpiTypes.find(k => k.value === type);
            return kpi?.text || 'KPI';
        },

        getKpiFormat(type) {
            const kpi = this.kpiTypes.find(k => k.value === type);
            return kpi?.format || '';
        },

        async onComparisonLevelChange() {
            if (!this.selectedComparisonLevel) {
                this.availableEntities = [];
                this.selectedEntities = [];
                return;
            }

            this.loadingEntities = true;
            try {
                // Chiamata API per ottenere le entità disponibili in base al livello selezionato
                const response = await axios.post(
                    this.$store.state.URL_GET_ENTITIES,
                    {
                        level: this.selectedComparisonLevel,
                        companyId: this.$store.state.company
                    }
                );
                
                this.availableEntities = response.data.map(entity => ({
                    text: entity.name,
                    value: entity.id
                }));
            } catch (error) {
                console.error('Errore nel caricamento delle entità:', error);
                this.availableEntities = [];
            } finally {
                this.loadingEntities = false;
            }
            this.selectedEntities = [];
        },

        selectKpiType(type) {
            this.selectedKpiType = type;
            const selectedKpi = this.kpiTypes.find(k => k.value === type);
            if (selectedKpi) {
                this.widgetTitle = selectedKpi.text;
            }
            // Prosegui automaticamente al passo 2 (configurazione)
            this.currentStep = 2;
        },

        async onDataLevelChange(level) {
            if (!level) {
                this.resetEntitySelections();
                return;
            }

            this.selectedDataLevel = level;
            this.loadingEntities = true;

            try {
                // Load clients for all levels since they all need client selection
                await this.loadClients();
            } catch (error) {
                console.error('Errore nel caricamento delle entità:', error);
            } finally {
                this.loadingEntities = false;
            }
        },

        resetEntitySelections() {
            // Reset single selections
            this.selectedClient = '';
            this.selectedBuilding = '';
            this.selectedFloor = '';
            this.selectedArea = '';
            // Reset multiple selections 
            this.selectedClients = [];
            this.selectedBuildings = [];
            this.selectedFloors = [];
            this.selectedAreas = [];
            this.selectedSensors = [];
            // Reset available options
            this.availableClients = [];
            this.availableBuildings = [];
            this.availableFloors = [];
            this.availableAreas = [];
            this.availableSensors = [];
            // Reset loading states
            this.loadingBuildings = false;
            this.loadingFloors = false;
            this.loadingAreas = false;
            this.loadingSensors = false;
        },

        async loadClients() {
            try {
                const response = await axios.post(this.$store.state.URL_GET_AVAILABLE_CLIENT);
                this.availableClients = response.data.map(el => ({
                    text: el.CompanyName,
                    value: el.id
                }));
            } catch (error) {
                console.error('Errore nel caricamento dei clienti:', error);
                this.availableClients = [];
            }
        },

        async loadBuildings() {
            if (!this.selectedClient) {
                this.availableBuildings = [];
                return;
            }

            try {
                const response = await axios.post(this.$store.state.URL_GET_AVAILABLE_BUILDINGS, {
                    companyId: this.selectedClient
                });
                this.availableBuildings = response.data.map(el => ({
                    text: el.BuildingName,
                    value: el.id
                }));
            } catch (error) {
                console.error('Errore nel caricamento degli edifici:', error);
                this.availableBuildings = [];
            }
        },

        async loadFloors() {
            if (!this.selectedBuilding) {
                this.availableFloors = [];
                return;
            }

            try {
                const response = await axios.post(this.$store.state.URL_GET_AVAILABLE_FLOORS, {
                    buildingId: this.selectedBuilding
                });
                this.availableFloors = response.data.map(el => ({
                    text: el.FloorName,
                    value: el.id
                }));
            } catch (error) {
                console.error('Errore nel caricamento dei piani:', error);
                this.availableFloors = [];
            }
        },

        async loadAreas() {
            if (!this.selectedFloor) {
                this.availableAreas = [];
                return;
            }

            try {
                const response = await axios.post(this.$store.state.URL_GET_AVAILABLE_AREAS, {
                    floorId: this.selectedFloor
                });
                this.availableAreas = response.data.map(el => ({
                    text: el.AreaName,
                    value: el.id
                }));
            } catch (error) {
                console.error('Errore nel caricamento delle aree:', error);
                this.availableAreas = [];
            }
        },

        async loadSensors() {
            if (!this.selectedArea) {
                this.availableSensors = [];
                return;
            }

            try {
                const response = await axios.post(this.$store.state.URL_GET_SENSORS_COMPANY_BUILDING_FLOOR_AREA, {
                    companyId: this.selectedClient,
                    buildingId: this.selectedBuilding,
                    floorId: this.selectedFloor,
                    areaId: this.selectedArea,
                    type: this.$route.params.type
                });
                this.availableSensors = response.data.map(el => ({
                    text: el.DispenserUnique,
                    value: el.id
                }));
            } catch (error) {
                console.error('Errore nel caricamento dei sensori:', error);
                this.availableSensors = [];
            }
        },

        validateKpiConfiguration() {
            return (
                this.selectedDataLevel &&
                this.selectedEntity &&
                this.selectedPeriod &&
                this.widgetSize
            );
        },

        showArticleTypeSelection() {
            return this.selectedKpiType && ['total_consumption', 'most_used_article'].includes(this.selectedKpiType);
        },

        dragStart() {
            this.isDragging = true;
        },

        dragEnd() {
            this.isDragging = false;
            this.saveWidgetPositions();
        },

        async saveWidgetPositions() {
            try {
                await axios.post(this.$store.state.URL_SAVE_WIDGET_POSITIONS, {
                    widgets: this.widgets.map((w, index) => ({
                        id: w.id,
                        position: index
                    }))
                });
            } catch (error) {
                console.error('Errore nel salvataggio delle posizioni:', error);
            }
        },

        async saveWidgetChanges() {
            if (!this.isEditFormValid) return;

            const updatedWidget = { ...this.editingWidget };

            try {
                if (updatedWidget.type === 'kpi') {
                    const kpiData = await this.fetchKpiData(updatedWidget);
                    
                    if (this.isTableKpi(updatedWidget.kpiType)) {
                        // Per KPI tabellari (Total Paper Consumption, Refill Frequency)
                        updatedWidget.tableData = kpiData.tableData || [];
                        updatedWidget.period = kpiData.period || 'Last 30 days';
                        updatedWidget.periodEnd = kpiData.periodEnd || new Date().toISOString();
                        updatedWidget.sortBy = this.getDefaultSortBy(updatedWidget.kpiType);
                    } else if (this.isNumericKpi(updatedWidget.kpiType)) {
                        updatedWidget.value = kpiData.value;
                        updatedWidget.label = kpiData.label;
                    } else {
                        // Per KPI basati su grafici
                        updatedWidget.series = kpiData.series || [];
                        updatedWidget.chartData = kpiData.chartData || {};
                    }
                }

                this.$set(this.widgets, this.editingWidgetIndex, updatedWidget);
                this.editDialog = false;
            } catch (error) {
                console.error('Errore durante l\'aggiornamento del widget:', error);
            }
        },

        changeWidgetSize(widget, newSize) {
            widget.size = newSize;
            
            // Forza il re-render del widget per applicare le nuove dimensioni
            this.$forceUpdate();
            
            this.$nextTick(() => {
                if (widget.type === 'chart' && this.$refs.apexChart) {
                    this.$refs.apexChart.updateOptions({
                        chart: {
                            height: this.getWidgetHeight(newSize)
                        }
                    });
                }
            });
        },

        onEditPeriodChange() {
            if (this.editingWidget.period !== 'custom') {
                this.editingWidget.startDate = null;
                this.editingWidget.endDate = null;
            }
        },

        defaultWidgetState() {
            return {
                title: '',
                type: null,
                size: 'medium',
                entities: [],
                dataLevel: null,
                articleType: null,
                startDate: null,
                endDate: null
            };
        },

        resetEditingWidget() {
            this.editingWidget = this.defaultWidgetState();
        },

        closeEditDialog() {
            this.editDialog = false;
            this.resetEditingWidget();
        },

        async onEditDataLevelChange(level) {
            if (!level) {
                this.editAvailableEntities = [];
                this.editingWidget.entityId = null;
                return;
            }
            
            this.editLoadingEntities = true;
            try {
                // Genera entità mock basate sul level per l'edit dialog
                this.editAvailableEntities = this.generateMockEntitiesForEdit(level);
            } catch (error) {
                console.error('Errore nel caricamento delle entità:', error);
                this.editAvailableEntities = [];
            } finally {
                this.editLoadingEntities = false;
            }
            
            // Non resettare entityId durante il caricamento per l'edit
            // this.editingWidget.entityId = null;
        },

        generateMockEntitiesForEdit(level) {
            // Genera entità mock simili a quelle usate per la creazione
            switch (level) {
                case 'client':
                    return [
                        { text: 'YouTissue', value: 1 },
                        { text: 'Client B', value: 2 },
                        { text: 'Client C', value: 3 }
                    ];
                case 'building':
                    return [
                        { text: 'Building A', value: 1 },
                        { text: 'Building B', value: 2 },
                        { text: 'Building C', value: 3 }
                    ];
                case 'floor':
                    return [
                        { text: 'Floor 1', value: 1 },
                        { text: 'Floor 2', value: 2 },
                        { text: 'Floor 3', value: 3 }
                    ];
                case 'area':
                    return [
                        { text: 'Area A', value: 1 },
                        { text: 'Area B', value: 2 },
                        { text: 'Area C', value: 3 }
                    ];
                case 'sensor':
                    return [
                        { text: 'DISP-137', value: 1 },
                        { text: 'DISP-143', value: 2 },
                        { text: 'DISP-166', value: 3 }
                    ];
                default:
                    return [];
            }
        },

        // Hierarchical selection methods
        async onHierarchicalClientChange(clientId) {
            // Reset dependent selections
            this.selectedBuilding = '';
            this.selectedFloor = '';
            this.selectedArea = '';
            this.selectedBuildings = [];
            this.selectedFloors = [];
            this.selectedAreas = [];
            this.selectedSensors = [];
            this.availableBuildings = [];
            this.availableFloors = [];
            this.availableAreas = [];
            this.availableSensors = [];

            if (!clientId) {
                return;
            }

            // Load buildings for the selected client
            this.loadingBuildings = true;
            try {
                const response = await axios.post(this.$store.state.URL_GET_AVAILABLE_BUILDINGS, {
                    companyId: clientId
                });
                this.availableBuildings = response.data.map(el => ({
                    text: el.BuildingName,
                    value: el.id
                }));
            } catch (error) {
                console.error('Errore nel caricamento degli edifici:', error);
                this.availableBuildings = [];
            } finally {
                this.loadingBuildings = false;
            }
        },

        async onHierarchicalBuildingChange(buildingId) {
            // Reset dependent selections
            this.selectedFloor = '';
            this.selectedArea = '';
            this.selectedFloors = [];
            this.selectedAreas = [];
            this.selectedSensors = [];
            this.availableFloors = [];
            this.availableAreas = [];
            this.availableSensors = [];

            if (!buildingId) {
                return;
            }

            // Load floors for the selected building
            this.loadingFloors = true;
            try {
                const response = await axios.post(this.$store.state.URL_GET_AVAILABLE_FLOORS, {
                    buildingId: buildingId
                });
                this.availableFloors = response.data.map(el => ({
                    text: el.FloorName,
                    value: el.id
                }));
            } catch (error) {
                console.error('Errore nel caricamento dei piani:', error);
                this.availableFloors = [];
            } finally {
                this.loadingFloors = false;
            }
        },

        async onHierarchicalFloorChange(floorId) {
            // Reset dependent selections
            this.selectedArea = '';
            this.selectedAreas = [];
            this.selectedSensors = [];
            this.availableAreas = [];
            this.availableSensors = [];

            if (!floorId) {
                return;
            }

            // Load areas for the selected floor
            this.loadingAreas = true;
            try {
                const response = await axios.post(this.$store.state.URL_GET_AVAILABLE_AREAS, {
                    floorId: floorId
                });
                this.availableAreas = response.data.map(el => ({
                    text: el.AreaName,
                    value: el.id
                }));
            } catch (error) {
                console.error('Errore nel caricamento delle aree:', error);
                this.availableAreas = [];
            } finally {
                this.loadingAreas = false;
            }
        },

        async onHierarchicalAreaChange(areaId) {
            // Reset dependent selections
            this.selectedSensors = [];
            this.availableSensors = [];

            if (!areaId) {
                return;
            }

            // Load sensors for the selected area
            this.loadingSensors = true;
            try {
                const response = await axios.post(this.$store.state.URL_GET_SENSORS_COMPANY_BUILDING_FLOOR_AREA, {
                    companyId: this.selectedClient,
                    buildingId: this.selectedBuilding,
                    floorId: this.selectedFloor,
                    areaId: areaId
                });
                this.availableSensors = response.data.map(el => ({
                    text: el.DisplayName || el.DispenserUnique,
                    value: el.id
                }));
            } catch (error) {
                console.error('Errore nel caricamento dei sensori:', error);
                this.availableSensors = [];
            } finally {
                this.loadingSensors = false;
            }
        },

        // Period selection methods
        onPeriodChange() {
            // Reset custom dates when switching away from custom
            if (this.selectedPeriod !== 'custom') {
                this.customStartDate = '';
                this.customEndDate = '';
            }
        },

        getYesterday() {
            const yesterday = new Date();
            yesterday.setDate(yesterday.getDate() - 1);
            return yesterday.toISOString().substr(0, 10);
        },

        getMinSelectableDate() {
            // Allow selection up to 30 days back for custom period
            const minDate = new Date();
            minDate.setDate(minDate.getDate() - 30);
            return minDate.toISOString().substr(0, 10);
        },

        getDateDifference(startDate, endDate) {
            const start = new Date(startDate);
            const end = new Date(endDate);
            const diffTime = Math.abs(end - start);
            return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        },

        getPeriodDisplayText() {
            const yesterday = this.getYesterday();
            const yesterdayFormatted = new Date(yesterday).toLocaleDateString('en-GB', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric'
            });

            switch (this.selectedPeriod) {
                case 'last_day':
                    return `Data from yesterday (${yesterdayFormatted}) - Up to ${yesterdayFormatted}`;
                case 'last_7_days':
                    return `Data from last 7 days - Up to ${yesterdayFormatted}`;
                case 'last_14_days':
                    return `Data from last 14 days - Up to ${yesterdayFormatted}`;
                case 'last_30_days':
                    return `Data from last 30 days - Up to ${yesterdayFormatted}`;
                case 'custom':
                    if (this.customStartDate && this.customEndDate) {
                        const startFormatted = new Date(this.customStartDate).toLocaleDateString('en-GB', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric'
                        });
                        const endFormatted = new Date(this.customEndDate).toLocaleDateString('en-GB', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric'
                        });
                        return `Custom period: ${startFormatted} to ${endFormatted} - Up to ${endFormatted}`;
                    }
                    return `Select custom date range - Up to ${yesterdayFormatted}`;
                default:
                    return `Up to ${yesterdayFormatted}`;
            }
        },

        getSelectedPeriodInfo() {
            // Returns period information for widget configuration
            switch (this.selectedPeriod) {
                case 'last_day':
                    return {
                        type: 'last_day',
                        days: 1,
                        endDate: this.getYesterday()
                    };
                case 'last_7_days':
                    return {
                        type: 'last_7_days',
                        days: 7,
                        endDate: this.getYesterday()
                    };
                case 'last_14_days':
                    return {
                        type: 'last_14_days',
                        days: 14,
                        endDate: this.getYesterday()
                    };
                case 'last_30_days':
                    return {
                        type: 'last_30_days',
                        days: 30,
                        endDate: this.getYesterday()
                    };
                case 'custom':
                    return {
                        type: 'custom',
                        startDate: this.customStartDate,
                        endDate: this.customEndDate,
                        days: this.customStartDate && this.customEndDate ? 
                              this.getDateDifference(this.customStartDate, this.customEndDate) : 0
                    };
                default:
                    return null;
            }
        },
    },

    watch: {
        // Watchers removed since hierarchical selection is now handled by onChange methods
        // The multiple selection system uses explicit change handlers for better control
    }
};
</script>

<style scoped>
.widget-container {
    transition: transform 0.2s ease;
}

.widget-card {
    display: flex;
    flex-direction: column;
    transition: all 0.3s ease;
    overflow: hidden;
}

.widget-card.small {
    min-height: 200px;
    max-height: 300px;
}

.widget-card.medium {
    min-height: 350px;
    max-height: 450px;
}

.widget-card.large {
    min-height: 500px;
    max-height: 600px;
}

.widget-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    padding: 16px !important;
}

.widget-title-section {
    flex-grow: 1;
    margin-right: 16px;
}

.widget-main-title {
    font-size: 1.1rem;
    font-weight: 500;
    color: #213A8F;
    line-height: 1.2;
    margin-bottom: 4px;
    display: flex;
    align-items: center;
}

.widget-subtitle {
    font-size: 0.85rem;
    color: #666;
    margin-bottom: 2px;
}

.widget-summary {
    font-size: 0.8rem;
    color: #999;
    display: flex;
    align-items: center;
}

.widget-actions {
    display: flex;
    gap: 4px;
}

.widget-action-btn {
    opacity: 0.7;
    transition: opacity 0.2s ease;
}

.widget-container:hover .widget-action-btn {
    opacity: 1;
}

.widget-content {
    flex-grow: 1;
    overflow-y: auto;
    padding: 16px !important;
}

.widget-card.small .widget-content {
    max-height: 220px;
}

.widget-card.medium .widget-content {
    max-height: 370px;
}

.widget-card.large .widget-content {
    max-height: 520px;
}

.ghost-widget {
    opacity: 0.5;
    background: #f5f5f5;
    border: 2px dashed #213A8F;
    border-radius: 4px;
}

.add-widget-card {
    cursor: pointer;
    border: 2px dashed #ccc;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.3s ease;
}

.add-widget-card:hover {
    border-color: #234a98;
    background-color: #f5f5f5;
}

.add-widget-text {
    font-size: 1.1rem;
    font-weight: 500;
    color: #213A8F;
}

.kpi-title {
    font-size: 1rem;
    font-weight: 500;
    color: #213A8F;
    line-height: 1.2;
}

.kpi-description {
    font-size: 0.85rem;
    color: #666;
    line-height: 1.3;
}

.kpi-container {
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
}

.kpi-value {
    font-size: 3rem;
    font-weight: bold;
    color: #234a98;
}

.kpi-label {
    font-size: 1.2rem;
    color: #666;
    margin-top: 1rem;
}

.widget-type-card {
    height: 100%;
    cursor: pointer;
    border: 2px solid transparent;
    transition: all 0.3s ease;
}

.widget-type-card:hover {
    border-color: #213A8F;
}

.widget-type-card.selected {
    border-color: #213A8F;
    background-color: #f5f5f5;
}

.headline {
    color: #213A8F;
    font-weight: 500;
}

.chart-template-card {
    cursor: pointer;
    border: 2px solid transparent;
    transition: all 0.3s ease;
    margin-bottom: 16px;
}

.chart-template-card:hover {
    border-color: #213A8F;
}

.chart-template-card.selected {
    border-color: #213A8F;
    background-color: #f5f5f5;
}

.chart-preview {
    background-color: #f5f5f5;
    border-right: 1px solid #eee;
}

.template-title {
    font-size: 1.1rem;
    font-weight: 500;
    color: #213A8F;
    margin-bottom: 8px;
}

.template-description {
    font-size: 0.9rem;
    color: #666;
    margin-bottom: 12px;
}

.template-details {
    display: flex;
    align-items: center;
}

.kpi-type-card {
    cursor: pointer;
    border: 2px solid transparent;
    transition: all 0.3s ease;
}

.kpi-type-card:hover {
    border-color: #213A8F;
    transform: translateY(-2px);
}

.kpi-type-card.selected {
    border-color: #213A8F;
    background-color: #f5f5f5;
}

.title {
    font-size: 1.1rem;
    font-weight: 500;
    color: #213A8F;
}

.caption {
    color: #666;
    line-height: 1.2;
}

.v-list-item-subtitle {
    white-space: normal;
    line-height: 1.2;
}

.font-italic {
    font-style: italic;
    color: #666;
    font-size: 0.85rem;
}

.widget-drag-handle {
    cursor: move;
    opacity: 0.5;
    transition: opacity 0.2s ease;
}

.widget-container:hover .widget-drag-handle {
    opacity: 1;
}



.widget-card {
    transition: all 0.3s ease;
}

.widget-card.dragging {
    transform: scale(1.02);
    box-shadow: 0 4px 8px rgba(0,0,0,0.1);
}

/* Table KPI Styles */
.table-kpi-container {
    height: 100%;
    display: flex;
    flex-direction: column;
    min-height: 0; /* Importante per flex shrinking */
}

.kpi-table {
    flex-grow: 1;
    flex-shrink: 1;
    min-height: 0; /* Importante per flex shrinking */
}

.kpi-table >>> .v-data-table__wrapper {
    overflow-y: auto;
    overflow-x: hidden;
}

/* Altezze specifiche per tabelle basate sulla dimensione del widget */
.widget-card.small .kpi-table >>> .v-data-table__wrapper {
    max-height: 120px;
}

.widget-card.medium .kpi-table >>> .v-data-table__wrapper {
    max-height: 250px;
}

.widget-card.large .kpi-table >>> .v-data-table__wrapper {
    max-height: 400px;
}

.kpi-table >>> th {
    font-size: 0.85rem !important;
    font-weight: 600 !important;
    color: #213A8F !important;
    padding: 8px 16px !important;
    border-bottom: 2px solid #213A8F !important;
}

.kpi-table >>> td {
    font-size: 0.9rem !important;
    padding: 12px 16px !important;
    border-bottom: 1px solid #e0e0e0 !important;
}

.kpi-table >>> tbody tr:hover {
    background-color: #f5f5f5 !important;
}

/* Trend Styles */
.trend-positive {
    color: #4CAF50 !important;
    font-weight: 600;
}

.trend-negative {
    color: #F44336 !important;
    font-weight: 600;
}

.trend-neutral {
    color: #9E9E9E !important;
    font-weight: 600;
}

/* Period Info */
.period-info {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: auto;
    padding-top: 12px;
    border-top: 1px solid #e0e0e0;
    font-size: 0.8rem;
    color: #666;
}

.period-label {
    font-weight: 500;
}

.period-date {
    color: #999;
}

/* Hierarchical Selection Styles */
.v-alert.info--text {
    background-color: #e3f2fd !important;
    border-left-color: #213A8F !important;
}

.v-alert.info--text .v-alert__content {
    color: #213A8F !important;
}

/* Multiple Selection Styles */
.v-select--is-menu-active .v-input__control {
    border-color: #213A8F !important;
}

.v-chip {
    background-color: #213A8F !important;
    color: white !important;
}

.v-chip .v-chip__close {
    color: white !important;
}

.v-chip .v-chip__close:hover {
    background-color: rgba(255, 255, 255, 0.2) !important;
}

.v-select.v-select--chips .v-select__selections {
    min-height: 42px;
}

.v-select.v-select--chips .v-chip {
    margin: 2px 4px 2px 0;
    font-size: 12px;
    height: 28px;
}

/* Loading indicator for hierarchical selects */
.v-select .v-progress-circular {
    color: #213A8F !important;
}

/* Improved spacing for multiple chips */
.v-text-field.v-text-field--outlined .v-select__selections {
    padding: 8px 12px;
}

/* Custom style for select all option */
.v-list-item--active .v-list-item__title {
    color: #213A8F !important;
    font-weight: 600;
}

/* Period Selection Styles */
.period-info-card {
    background-color: #f8f9fa !important;
    border-color: #213A8F !important;
    border-width: 1px !important;
}

.period-info-text {
    display: flex;
    align-items: center;
    font-size: 0.9rem;
    color: #666 !important;
}

.period-info-card .v-card__text {
    padding: 12px 16px !important;
}

/* Custom period validation alerts */
.v-alert.success--text {
    background-color: #e8f5e8 !important;
    border-left-color: #4CAF50 !important;
}

.v-alert.warning--text {
    background-color: #fff3e0 !important;
    border-left-color: #FF9800 !important;
}

.v-alert.error--text {
    background-color: #ffebee !important;
    border-left-color: #F44336 !important;
}

/* Date picker styling */
.v-menu .v-picker {
    border-radius: 4px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

/* Heatmap Widget Styling */
.widget-card .apexcharts-heatmap-rect {
    cursor: pointer;
    stroke: rgba(255, 255, 255, 0.8);
    stroke-width: 1;
}

.widget-card .apexcharts-heatmap-rect:hover {
    stroke: rgba(255, 255, 255, 1);
    stroke-width: 2;
    filter: brightness(1.1);
}

.widget-card .apexcharts-tooltip {
    border-radius: 6px !important;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
}

.v-date-picker-header {
    background-color: #213A8F !important;
}

.v-date-picker-header .v-btn {
    color: white !important;
}

/* Date input focus state */
.v-text-field.v-text-field--focused .v-input__control {
    border-color: #213A8F !important;
}

.v-text-field.v-text-field--focused .v-input__icon--prepend-inner {
    color: #213A8F !important;
}

/* Refill Frequency Value Styling */
.refill-value {
    font-weight: 600;
    color: #213A8F;
    font-size: 0.95rem;
}

/* Packs Used Value Styling */
.packs-value {
    font-weight: 600;
    color: #213A8F;
    font-size: 0.95rem;
}

/* Entries Value Styling */
.entries-value {
    font-weight: 600;
    color: #009CAB;
    font-size: 0.95rem;
}

/* Status Chip Styling */
.status-chip {
    min-width: 70px;
    justify-content: center;
}

.status-chip .v-chip__content {
    font-size: 0.8rem;
    font-weight: 600;
}

/* Alerts Value Styling */
.alerts-value {
    font-weight: 600;
    color: #FF9800;
    font-size: 0.95rem;
}

/* Widget Title Input Styling */
.v-text-field input {
    color: #2c3e50 !important;
    font-weight: 500 !important;
    font-size: 1rem !important;
}

.v-text-field .v-label {
    color: #666 !important;
    font-weight: 500 !important;
}

.v-text-field .v-label--active {
    color: #213A8F !important;
    font-weight: 600 !important;
}

/* Enhanced input field visibility */
.v-text-field--outlined .v-input__control {
    background-color: #fff !important;
}

.v-text-field--outlined input {
    color: #2c3e50 !important;
    font-weight: 500 !important;
}

/* Focus state for better visibility */
.v-text-field--outlined.v-input--is-focused .v-input__control {
    border-color: #213A8F !important;
    border-width: 2px !important;
}

.v-text-field--outlined.v-input--is-focused input {
    color: #213A8F !important;
    font-weight: 600 !important;
}

/* Specific styling for Widget Title Input */
.widget-title-input input {
    color: #2c3e50 !important;
    font-weight: 600 !important;
    font-size: 1.1rem !important;
    text-shadow: 0 0 1px rgba(44, 62, 80, 0.3) !important;
}

.widget-title-input .v-label {
    color: #213A8F !important;
    font-weight: 600 !important;
}

.widget-title-input .v-label--active {
    color: #213A8F !important;
    font-weight: 700 !important;
    transform: translateY(-18px) scale(0.75) !important;
}

.widget-title-input.v-text-field--outlined .v-input__control {
    background-color: #ffffff !important;
    border: 2px solid #e0e0e0 !important;
}

.widget-title-input.v-text-field--outlined.v-input--is-focused .v-input__control {
    border-color: #213A8F !important;
    border-width: 2px !important;
    box-shadow: 0 0 0 2px rgba(33, 58, 143, 0.1) !important;
}
</style> 